import { Routes, Route, Link, Navigate, useParams } from 'react-router-dom'
import { useEffect, useState, useMemo, lazy, Suspense } from 'react'
import { supabase } from './lib/supabase'
import { downloadExportZip } from './export'
// ADR-036 §S35 amendment Block 5: cluster-color reveal on the attendee's
// group card. Imports from src/projection/colors.js — pure-math module
// (no Three.js, no React, no Supabase per its header), safe for the
// /join chunk. Does NOT pull the lazy-loaded PresentProjection bundle.
import { computeClusterColors, hslString, pickTextColor } from './projection/colors.js'

// ADR-034 §Consequences: projection bundle (~600KB three.js) must NOT
// load on /join, /admin, etc. React.lazy + Vite route-boundary splitting
// keeps three.js out of every other chunk. PresentProjection lives in
// src/projection/ specifically to give Vite a clean module-graph cut.
const PresentProjection = lazy(() => import('./projection/PresentProjection.jsx'))

// Route tree.
//   /              Landing (public page; nothing to do here)
//   /join          Persistent ADR-013 join URL; resolves to live session and
//                  runs the full attendee flow: form → opt-in → group
//   /admin/login   Admin magic-link sign-in (S21 §P1 item 2, ADR-029).
//                  Reuses admin_users + is_admin() from _001 + _007.
//   /admin         Guarded admin landing (S21 §P1 item 3, ADR-029).
//                  AdminGuard checks session + is_admin() RPC; renders
//                  children on pass, redirects to /admin/login on miss.
//   /admin/sessions/new
//                  Session creation form (S21 §P1 item 4, ADR-029). Behind
//                  AdminGuard. Posts to create-session edge fn (item 5,
//                  pending). Success screen shows organizer URL for
//                  sharing with facilitator.
//   /present/:organizer_token
//                  3D brain projection surface (ADR-034/035/036, S26
//                  Block 3). Behind AdminGuard, lazy-loaded so three.js
//                  stays out of /join + admin bundles. Block 3 ships
//                  scaffolding only — neurons (ADR-035 §3-6), bridges
//                  (ADR-039), and state-machine treatment (ADR-037)
//                  deferred.
//   /_probe        Dev-only: Phase 1 acceptance probe against Supabase
//   *              404
//
// Still to come:
//   create-session  S21 §P1 items 5–6: edge function + config.toml block.
//                   Form POSTs to it; not deployed yet. Renamed surface
//                   per ADR-029 (was /organize/:token/new).
//   /organize/:token        Phase 5 later: organizer runtime view.
//   /present/:token         Phase 6.
//
// History:
//   src/App-v1.jsx — Session 1 scaffold (48 lines)
//   src/App-v2.jsx — Session 5 rewrite: /join + JoinSubmissionSkeleton + /_probe
//   src/App-v3.jsx — Session 6 pre-readErrorEnvelope archive
//   src/App-v4.jsx — Session 10 pre-realtime-wiring archive (static GroupSkeleton)
//   src/App-v5.jsx — Session 12 archive (realtime wiring, pre-ADR-021 JWT)
//   src/App-v6.jsx — Session 13 archive (ADR-021 attendee JWT mint + setSession +
//                    realtime + RLS-gated DB selects in GroupSkeleton).
//                    Moved to logs/superseded/App-v6-pre-ADR-023.jsx in S18 per
//                    ADR-023 supersession audit (B3); the in-tree path is dead.
//   src/App-v7.jsx — Session 14 (v7): ADR-023. The attendee JWT path is gone.
//                    submit-topic no longer returns access_token; the client
//                    no longer calls setSession or supabase.realtime.setAuth.
//                    GroupSkeleton's three effects (group SELECT + groups
//                    realtime, members SELECT + submissions realtime, own-row
//                    UPDATE realtime + 5s fallback poll) are collapsed into
//                    one effect that polls the get-my-group edge function
//                    every 2 s with the submission_id. The edge function is
//                    the entire attendee read surface; no direct DB reads
//                    from the browser anymore. The submissions_attendee_select
//                    / groups_attendee_select RLS policies and their helper
//                    functions were retired by migration _009. Snapshotted
//                    in S20 before the v8 picker build.
//   src/App-v8.jsx — Session 20 (v8): ADR-027 + ADR-019 §2 + ADR-025 §4/§5
//                    + ADR-028. GroupSkeleton gains the Phase 4 switch-group
//                    picker. get-my-group now returns session_groups; the
//                    same 2 s poll drives both the group card and the
//                    picker. Rows render name + member_count; archived rows
//                    (archived_at non-null) filtered client-side; current
//                    group badged "Current". No "Full" badge — ADR-028
//                    retired the cap at switch time (cap is classifier-only).
//                    Tap posts to switch-group and waits for the next poll
//                    to reconcile — no optimistic update per ADR-023
//                    polling semantics. Error envelope (target_archived)
//                    renders inline on the tapped row; same_group
//                    suppressed; not_attendee / session_not_live logged
//                    and fail silent. cap_reached is no longer emitted.
//                    Snapshotted in S21 before the v9 admin-surface build.
//   src/App-v9.jsx — Session 21 (v9): adds /admin/login (§P1 item 2),
//                    /admin guarded landing (item 3), /admin/sessions/new
//                    creation form (item 4) per ADR-029. Snapshotted in
//                    S22 before the v10 ADR-031 build.
//   src/App-v10.jsx — Session 22 (v10): ADR-031.
//                    - JoinFlow gains an optional `intent` state. When the
//                      user clicks [Change my topic] from GroupSkeleton we
//                      route them back to the form pre-filled with their
//                      previous email + topic and set intent='resubmit'.
//                      handleSubmit forwards intent to submit-topic; on
//                      successful resubmit (data.resubmit === true) we skip
//                      the opt-in screen (the user already chose) and go
//                      straight to GroupSkeleton with the new submission_id.
//                    - SubmissionForm gains an `isResubmit` prop that
//                      relabels the submit button + shows a small contextual
//                      note. No layout change.
//                    - GroupSkeleton's soft "You're already in this session"
//                      message is replaced with an explicit duplicate card
//                      per ADR-031 §Decision 5. A [Change my topic] button
//                      is exposed at the bottom of every group view (not
//                      only the duplicate path) — first-time attendees who
//                      decide their topic was wrong have the same recourse
//                      as duplicates.
//                    - New /admin/sessions/:organizer_token route
//                      (AdminSessionDetail). Behind AdminGuard. Loads the
//                      session by organizer_token, lists its active
//                      submissions with per-row [Delete] (calls
//                      delete-submission edge fn — soft-delete, audit
//                      preserved), and exposes a [Reset Session] action
//                      with a code-typing confirm dialog (calls reset-session
//                      edge fn — hard-delete of submissions + groups,
//                      session row + config preserved).
//                    - AdminCreateSuccess gets a "Manage this session" link
//                      to the new admin detail page so the admin doesn't
//                      have to remember the organizer_token to navigate.
//                    Snapshotted in S25 before the v11 ADR-043 config form build.
//   src/App.jsx    — Session 25 (v11): ADR-043 session runtime config surface.
//                    - New /admin/sessions/:organizer_token/config route
//                      (AdminSessionConfig). Behind AdminGuard. Loads all 22
//                      ADR-043 runtime parameters from the sessions row and
//                      writes changes via update-session-config edge fn
//                      (ADR-029/030 admin pattern). Audit trail lands in
//                      session_config_changes (migration _019).
//                    - Form renders 6 collapsible sections (state machine /
//                      classifier / auto-merge / split / projection /
//                      bridges). Default expanded: state machine + classifier
//                      per spec §2. Sticky save bar at bottom (§7 item 3).
//                      Strict cross-field validation surfaced via edge-fn
//                      field_errors array (§7 item 1). Projection fields
//                      editable pre-ship (§7 item 2). No per-field reset
//                      affordance (§7 item 4).
//                    - AdminSessionDetail header gains an "Edit runtime
//                      config →" link so the admin discovers the new page
//                      without typing the URL.
//                    - CONFIG_FIELDS table in this file mirrors the edge-fn
//                      FIELDS array in update-session-config/index.ts —
//                      gates, bounds, and minute-↔-seconds converters must
//                      stay in sync across both edges.

// ---------------------------------------------------------------------------
// Shared frame + small helpers
// ---------------------------------------------------------------------------

function JoinFrame({ children }) {
  return (
    <main className="min-h-screen bg-hap-bg text-hap-ink font-sans flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-xl w-full space-y-6">
        <header className="space-y-2">
          <p className="text-sm uppercase tracking-widest text-hap-muted">Human AI Partners</p>
          <h1 className="text-3xl font-semibold leading-tight">Unconference</h1>
        </header>
        {children}
      </div>
    </main>
  )
}

function formatWhen(iso) {
  if (!iso) return null
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })
  } catch {
    return iso
  }
}

// ADR-015: identifier shown to other group members is the truncated email.
//   `[first 4 chars of local-part]***@[full domain]`
//   For local parts under 4 chars: `ab***@example.com`
export function truncateEmail(email) {
  if (typeof email !== 'string' || !email.includes('@')) return email
  const [local, ...rest] = email.split('@')
  const domain = rest.join('@')
  const head = local.slice(0, Math.min(4, local.length))
  return `${head}***@${domain}`
}

// ---------------------------------------------------------------------------
// Landing + 404
// ---------------------------------------------------------------------------

function Landing() {
  return (
    <main className="min-h-screen bg-hap-bg text-hap-ink font-sans flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-xl w-full space-y-6">
        <header className="space-y-2">
          <p className="text-sm uppercase tracking-widest text-hap-muted">Human AI Partners</p>
          <h1 className="text-3xl font-semibold leading-tight">Unconference</h1>
        </header>
        <p className="text-hap-muted leading-relaxed">
          A live clustering tool for unconference sessions. Built for Boulder Startup Week 2026.
        </p>
        <p className="text-sm text-hap-muted">
          Session links are distributed at the event. Nothing to do here.
        </p>
        <footer className="pt-6 border-t border-hap-border text-xs text-hap-muted">
          <Link to="/" className="underline underline-offset-2 hover:text-hap-ink">humanaipartners.ai</Link>
        </footer>
      </div>
    </main>
  )
}

function NotFound() {
  return (
    <main className="min-h-screen bg-hap-bg text-hap-ink font-sans flex items-center justify-center px-6">
      <div className="text-center space-y-2">
        <p className="text-xs uppercase tracking-widest text-hap-muted">404</p>
        <h1 className="text-2xl font-semibold">Page not found</h1>
        <Link to="/" className="text-sm underline underline-offset-2">Back to home</Link>
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// /admin/login — admin magic-link sign-in (ADR-029, S21 §P1 item 2)
//   Reuses the shared supabase client. signInWithOtp triggers a Supabase Auth
//   email containing a magic-link URL. emailRedirectTo matches the Redirect
//   URLs whitelist configured in the dashboard (S21 §P1 item 1).
//
//   Any email can request a link — admin_users is RLS-protected, so the anon
//   client cannot pre-check membership. The /admin route guard (item 3)
//   enforces is_admin(email); non-admin sessions are denied at the guard.
// ---------------------------------------------------------------------------

function AdminLogin() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState(null)
  const [sentTo, setSentTo] = useState(null)

  async function onSubmit(e) {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    setStatus('submitting')
    setError(null)
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: { emailRedirectTo: `${window.location.origin}/admin` },
      })
      if (otpError) {
        setError(otpError.message || 'Could not send sign-in link. Try again.')
        setStatus('error')
        return
      }
      setSentTo(trimmed)
      setStatus('sent')
    } catch (err) {
      setError(err?.message || 'Unexpected error. Try again.')
      setStatus('error')
    }
  }

  if (status === 'sent') {
    return (
      <main className="min-h-screen bg-hap-bg text-hap-ink font-sans flex flex-col items-center justify-center px-6 py-12">
        <div className="max-w-xl w-full space-y-6">
          <header className="space-y-2">
            <p className="text-sm uppercase tracking-widest text-hap-muted">Human AI Partners</p>
            <h1 className="text-3xl font-semibold leading-tight">Check your email</h1>
          </header>
          <p className="text-hap-muted leading-relaxed">
            A sign-in link has been sent to <span className="text-hap-ink font-medium">{sentTo}</span>. Click the link in that email to finish signing in.
          </p>
          <p className="text-sm text-hap-muted">
            Links expire quickly. If you don't see the email, check spam. You can request another in about a minute.
          </p>
          <button
            type="button"
            onClick={() => { setStatus('idle'); setEmail(''); setSentTo(null); setError(null); }}
            className="text-sm underline underline-offset-2 text-hap-muted hover:text-hap-ink"
          >
            Use a different email
          </button>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-hap-bg text-hap-ink font-sans flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-xl w-full space-y-6">
        <header className="space-y-2">
          <p className="text-sm uppercase tracking-widest text-hap-muted">Human AI Partners</p>
          <h1 className="text-3xl font-semibold leading-tight">Admin sign-in</h1>
        </header>
        <p className="text-hap-muted leading-relaxed">
          Enter your admin email. We'll send a sign-in link.
        </p>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <label className="block space-y-2">
            <span className="text-sm font-medium">Email</span>
            <input
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === 'submitting'}
              className="w-full rounded border border-hap-border bg-hap-surface px-3 py-2 text-hap-ink placeholder:text-hap-muted focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50"
              placeholder="you@example.com"
            />
          </label>
          {error ? (
            <p role="alert" className="text-sm text-hap-danger">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={status === 'submitting' || !email.trim()}
            className="w-full rounded bg-hap-accent px-4 py-2 font-medium text-white disabled:opacity-50"
          >
            {status === 'submitting' ? 'Sending…' : 'Send sign-in link'}
          </button>
        </form>
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// /admin — guarded admin landing (ADR-029, S21 §P1 item 3)
//   AdminGuard wraps children:
//     1. getSession(); if none → <Navigate to="/admin/login">.
//     2. supabase.rpc('is_admin') (SECURITY DEFINER, no args, reads
//        auth.jwt() ->> 'email' server-side; granted to authenticated);
//        false → AdminNotAuthorized with sign-out.
//     3. true → render children.
//   onAuthStateChange re-runs the check on sign-out / token refresh.
// ---------------------------------------------------------------------------

function AdminFrame({ title, children }) {
  return (
    <main className="min-h-screen bg-hap-bg text-hap-ink font-sans flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-xl w-full space-y-6">
        <header className="space-y-2">
          <p className="text-sm uppercase tracking-widest text-hap-muted">Human AI Partners</p>
          <h1 className="text-3xl font-semibold leading-tight">{title}</h1>
        </header>
        {children}
      </div>
    </main>
  )
}

function AdminGuard({ children }) {
  const [state, setState] = useState('loading')
  const [email, setEmail] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function check() {
      const { data: sessionData } = await supabase.auth.getSession()
      if (cancelled) return
      const session = sessionData?.session
      if (!session) {
        setEmail(null)
        setState('no_session')
        return
      }
      setEmail(session.user?.email || null)
      const { data: isAdmin, error: rpcError } = await supabase.rpc('is_admin')
      if (cancelled) return
      if (rpcError) {
        setState('error')
        return
      }
      setState(isAdmin ? 'admin' : 'not_admin')
    }
    check()
    const { data: sub } = supabase.auth.onAuthStateChange(() => { check() })
    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  if (state === 'loading') {
    return (
      <AdminFrame title="Admin">
        <p className="text-hap-muted" role="status" aria-live="polite">
          Checking your sign-in…
        </p>
      </AdminFrame>
    )
  }

  if (state === 'no_session') {
    return <Navigate to="/admin/login" replace />
  }

  if (state === 'not_admin') {
    return <AdminNotAuthorized email={email} />
  }

  if (state === 'error') {
    return (
      <AdminFrame title="Admin">
        <p className="text-hap-danger" role="alert">
          Could not verify admin status. Reload the page to try again.
        </p>
      </AdminFrame>
    )
  }

  return children
}

function AdminNotAuthorized({ email }) {
  async function handleSignOut() {
    await supabase.auth.signOut()
  }
  return (
    <AdminFrame title="Not authorized">
      <p className="text-hap-muted leading-relaxed">
        You are signed in as <span className="text-hap-ink font-medium">{email}</span>, but this email isn't on the admin allowlist.
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleSignOut}
          className="rounded bg-hap-accent px-4 py-2 font-medium text-white"
        >
          Sign out
        </button>
        <Link
          to="/"
          className="self-center text-sm underline underline-offset-2 text-hap-muted hover:text-hap-ink"
        >
          Back to home
        </Link>
      </div>
    </AdminFrame>
  )
}

// AdminLanding — the dashboard. Shows every session (setup/live/ended),
// the global "Export all" button, and Create session. ADR-033 folds the
// sessions list into the landing instead of creating a /admin/sessions
// route; the back button from session detail lands you here on a real
// list, not a near-empty splash.
function AdminLanding() {
  const [email, setEmail] = useState(null)
  const [sessions, setSessions] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [exportPending, setExportPending] = useState(false)
  const [exportError, setExportError] = useState(null)
  const [exportInfo, setExportInfo] = useState(null)

  async function loadSessions() {
    setLoadError(null)
    const { data, error } = await supabase
      .from('sessions')
      .select('id, code, name, facilitator_name, state, organizer_token, total_submissions, total_groups, opt_in_count, created_at, export_delivered_at')
      .order('created_at', { ascending: false })
    if (error) {
      setLoadError(error.message)
      setSessions([])
      return
    }
    setSessions(data || [])
  }

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setEmail(data?.session?.user?.email || null)
    })
    loadSessions()
    return () => { cancelled = true }
  }, [])

  async function handleSignOut() {
    await supabase.auth.signOut()
  }

  async function handleExportAll() {
    setExportPending(true)
    setExportError(null)
    setExportInfo(null)
    try {
      const { data, error } = await supabase.functions.invoke('export-session-data', {
        body: {},
      })
      if (error) {
        setExportError(`Export failed: ${error.message || 'unknown error'}`)
        setExportPending(false)
        return
      }
      if (!data?.ok) {
        setExportError(`Export failed: ${data?.error || 'unexpected response'}`)
        setExportPending(false)
        return
      }
      const filename = await downloadExportZip(data)
      setExportInfo({
        filename,
        session_count: data.meta?.session_count ?? 0,
        submission_count: data.meta?.submission_count ?? 0,
        redacted_email_count: data.meta?.redacted_email_count ?? 0,
      })
      setExportPending(false)
    } catch (e) {
      setExportError(`Export threw: ${String(e)}`)
      setExportPending(false)
    }
  }

  const frame = (children) => (
    <main className="min-h-screen bg-hap-bg text-hap-ink font-sans px-6 py-12">
      <div className="max-w-3xl w-full mx-auto space-y-6">
        <header className="space-y-2">
          <p className="text-sm uppercase tracking-widest text-hap-muted">Human AI Partners</p>
          <h1 className="text-3xl font-semibold leading-tight">Admin</h1>
          <p className="text-hap-muted leading-relaxed">
            Signed in as <span className="text-hap-ink font-medium">{email || '…'}</span>.
          </p>
        </header>
        {children}
      </div>
    </main>
  )

  return frame(
    <>
      <div className="flex flex-wrap gap-3">
        <Link
          to="/admin/sessions/new"
          className="rounded bg-hap-accent px-4 py-2 font-medium text-white"
        >
          Create session
        </Link>
        <button
          type="button"
          onClick={handleExportAll}
          disabled={exportPending}
          className="rounded border border-hap-border bg-white px-4 py-2 font-medium text-hap-ink hover:bg-hap-border disabled:opacity-50"
        >
          {exportPending ? 'Building export…' : 'Export all sessions'}
        </button>
        <button
          type="button"
          onClick={handleSignOut}
          className="rounded border border-hap-border bg-hap-surface px-4 py-2 font-medium text-hap-ink hover:bg-hap-border ml-auto"
        >
          Sign out
        </button>
      </div>

      {exportError ? (
        <p role="alert" className="text-sm text-hap-danger">{exportError}</p>
      ) : null}
      {exportInfo ? (
        <p role="status" aria-live="polite" className="text-sm text-hap-muted">
          Downloaded <code className="text-hap-ink">{exportInfo.filename}</code>
          {' '}({exportInfo.session_count} session{exportInfo.session_count === 1 ? '' : 's'},
          {' '}{exportInfo.submission_count} submission{exportInfo.submission_count === 1 ? '' : 's'},
          {' '}{exportInfo.redacted_email_count} email{exportInfo.redacted_email_count === 1 ? '' : 's'} redacted).
        </p>
      ) : null}

      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold">
            Sessions{' '}
            <span className="text-sm font-normal text-hap-muted">
              ({sessions == null ? '…' : sessions.length})
            </span>
          </h2>
          <button
            type="button"
            onClick={loadSessions}
            className="text-sm underline underline-offset-2 text-hap-muted hover:text-hap-ink"
          >
            Refresh
          </button>
        </div>

        {loadError ? (
          <p role="alert" className="text-sm text-hap-danger">Could not load sessions: {loadError}</p>
        ) : null}

        {sessions == null ? (
          <p className="text-sm text-hap-muted" role="status" aria-live="polite">Loading sessions…</p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-hap-muted">No sessions yet. Create one to get started.</p>
        ) : (
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li key={s.id} className="rounded border border-hap-border bg-hap-surface p-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <Link
                    to={`/admin/sessions/${s.organizer_token}`}
                    className="font-mono text-sm text-hap-ink underline underline-offset-2 hover:text-hap-accent"
                  >
                    {s.code}
                  </Link>
                  <span className="text-sm text-hap-ink">{s.name}</span>
                  <span
                    className={
                      'text-xs font-mono px-2 py-0.5 rounded ' +
                      (s.state === 'live'
                        ? 'bg-hap-accent text-white'
                        : s.state === 'ended'
                          ? 'bg-hap-border text-hap-muted'
                          : 'bg-white text-hap-muted border border-hap-border')
                    }
                  >
                    {s.state}
                  </span>
                  <span className="text-xs text-hap-muted ml-auto">
                    {s.total_submissions ?? 0} submissions · {s.total_groups ?? 0} groups · {s.opt_in_count ?? 0} opted in
                  </span>
                </div>
                <div className="mt-1 text-xs text-hap-muted">
                  Facilitator: {s.facilitator_name || '—'} · Created {new Date(s.created_at).toLocaleString()}
                  {s.export_delivered_at
                    ? ` · Exported ${new Date(s.export_delivered_at).toLocaleString()}`
                    : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  )
}

// ---------------------------------------------------------------------------
// /admin/sessions/new — session creation form (ADR-029, S21 §P1 item 4;
//   ADR-044 wall-clock pivot landed S25 2026-04-24)
//
//   Admin-only (AdminGuard). Posts to create-session edge fn. On success,
//   renders organizer URL for copy + share with the facilitator.
//
//   Form fields:
//     - name                       required, text
//     - code                       required, text, freeform
//     - facilitator_name           required, text
//     - start_time                 datetime-local, default now
//     - submission_window_ends_at  datetime-local, default start + 5 min  (ADR-044)
//     - discussion_ends_at         datetime-local, default start + 45 min (ADR-044)
//     - timezone                   IANA string, default browser TZ        (ADR-044 §Decision 3)
//     - max_members_per_group      int, default 8
//     - merge_check_enabled        checkbox, default true (ADR-026)
//
//   Silent server defaults (not in form): wrap_warning_minutes=5,
//   max_groups=30. Initial state=setup. Go-live is a separate flow.
// ---------------------------------------------------------------------------

function formatLocalDateTimeInput(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

function addMinutes(date, minutes) {
  return new Date(date.getTime() + minutes * 60_000)
}

function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Denver'
  } catch {
    return 'America/Denver'
  }
}

// Render a timestamptz ISO string in the session's IANA timezone.
// ADR-044 §Decision 3 — all admin/attendee UIs render session timestamps
// in the session's timezone, not the viewer's. Returns an em-dash when
// the value is null (session still in setup).
function formatSessionTime(iso, timezone) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString('en-US', {
      timeZone: timezone || 'America/Denver',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    })
  } catch {
    return iso
  }
}

function defaultCreateSessionForm() {
  const now = new Date()
  return {
    name: '',
    code: '',
    facilitator_name: '',
    start_time: formatLocalDateTimeInput(now),
    submission_window_ends_at: formatLocalDateTimeInput(addMinutes(now, 5)),
    discussion_ends_at: formatLocalDateTimeInput(addMinutes(now, 45)),
    timezone: browserTimezone(),
    max_members_per_group: 8,
    merge_check_enabled: true,
  }
}

function friendlyCreateSessionError(code, detail) {
  switch (code) {
    case 'code_in_use':
      return 'That session code is already in use. Pick a different code.'
    case 'not_admin':
      return 'Not authorized to create sessions.'
    case 'invalid_body':
      return detail ? `Invalid input: ${detail}` : 'Some fields are invalid. Check the form.'
    case 'invalid_start_time':
      return 'Start time is not a valid date.'
    case 'invalid_submission_window':
      return detail
        ? `Submission window: ${detail}`
        : 'Submission window end time must be after start time.'
    case 'invalid_discussion_ends_at':
      return detail
        ? `Discussion end: ${detail}`
        : 'Discussion end time must be after the submission window ends.'
    case 'invalid_timezone':
      return detail || 'That is not a recognized timezone. Use an IANA name like "America/Denver".'
    case 'auth_missing':
      return 'Not signed in. Reload the page and sign in again.'
    case 'db_error':
      return 'Database error. Try again in a moment.'
    case 'server_misconfigured':
      return 'Server is misconfigured. Check the edge function environment.'
    case 'method_not_allowed':
      return 'Unexpected request shape. Reload and try again.'
    case 'invalid_json':
      return 'Request body did not parse. Reload and try again.'
    case 'unexpected':
      return detail ? `Unexpected error: ${detail}` : 'Unexpected error. Try again.'
    default:
      // S33 followups #018: previously returned null, which let the caller's
      // fallback chain surface the unhelpful supabase-js wrapper message
      // ("Edge Function returned a non-2xx status code"). Friendly fallback
      // covers any unmapped error code; surfaces detail if the edge function
      // included one, otherwise generic with a hint to check function logs.
      return detail
        ? `Could not create session: ${detail}`
        : 'Could not create session. Server returned an unexpected error. Try again or check the function logs.'
  }
}

function AdminCreateSession() {
  const [form, setForm] = useState(defaultCreateSessionForm)
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  function onField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function resetForm() {
    setForm(defaultCreateSessionForm())
    setError(null)
    setResult(null)
    setStatus('idle')
  }

  async function onSubmit(e) {
    e.preventDefault()
    setError(null)
    setStatus('submitting')
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        facilitator_name: form.facilitator_name.trim(),
        start_time: new Date(form.start_time).toISOString(),
        submission_window_ends_at: new Date(form.submission_window_ends_at).toISOString(),
        discussion_ends_at: new Date(form.discussion_ends_at).toISOString(),
        timezone: form.timezone.trim(),
        max_members_per_group: Number(form.max_members_per_group),
        merge_check_enabled: Boolean(form.merge_check_enabled),
      }
      const { data, error: fnError } = await supabase.functions.invoke('create-session', {
        body: payload,
      })
      if (fnError) {
        // supabase-js v2 wraps 4xx/5xx bodies on error.context; unwrap to
        // reveal the real error code (e.g. code_in_use, not_admin,
        // invalid_body with detail).
        const envelope = await readErrorEnvelope(fnError)
        const friendly = friendlyCreateSessionError(envelope?.error, envelope?.detail)
        setError(friendly || fnError.message || 'Could not create session. Try again.')
        setStatus('error')
        return
      }
      if (!data?.id || !data?.organizer_token || !data?.organizer_url) {
        setError('Unexpected response from create-session. Check the edge function logs.')
        setStatus('error')
        return
      }
      setResult(data)
      setStatus('success')
    } catch (err) {
      setError(err?.message || 'Unexpected error. Try again.')
      setStatus('error')
    }
  }

  if (status === 'success' && result) {
    return <AdminCreateSuccess result={result} onCreateAnother={resetForm} />
  }

  const disabled = status === 'submitting'

  return (
    <AdminFrame title="Create session">
      <form onSubmit={onSubmit} className="space-y-5">
        <label className="block space-y-2">
          <span className="text-sm font-medium">Name</span>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => onField('name', e.target.value)}
            disabled={disabled}
            className="w-full rounded border border-hap-border bg-hap-surface px-3 py-2 text-hap-ink placeholder:text-hap-muted focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50"
            placeholder="BSW 2026 Monday morning"
          />
        </label>

        <label className="block space-y-2">
          <span className="text-sm font-medium">Session code</span>
          <input
            type="text"
            required
            value={form.code}
            onChange={(e) => onField('code', e.target.value)}
            disabled={disabled}
            className="w-full rounded border border-hap-border bg-hap-surface px-3 py-2 text-hap-ink placeholder:text-hap-muted focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50"
            placeholder="BSW-001"
          />
          <span className="text-xs text-hap-muted">Freeform. Must be unique across sessions.</span>
        </label>

        <label className="block space-y-2">
          <span className="text-sm font-medium">Facilitator name</span>
          <input
            type="text"
            required
            value={form.facilitator_name}
            onChange={(e) => onField('facilitator_name', e.target.value)}
            disabled={disabled}
            className="w-full rounded border border-hap-border bg-hap-surface px-3 py-2 text-hap-ink placeholder:text-hap-muted focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50"
            placeholder="Your name"
          />
        </label>

        <label className="block space-y-2">
          <span className="text-sm font-medium">Planned start time</span>
          <input
            type="datetime-local"
            required
            value={form.start_time}
            onChange={(e) => onField('start_time', e.target.value)}
            disabled={disabled}
            className="w-full rounded border border-hap-border bg-hap-surface px-3 py-2 text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50"
          />
          <span className="text-xs text-hap-muted">Defaults to now. Adjust if scheduling a future session.</span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-2">
            <span className="text-sm font-medium">Submission window closes</span>
            <input
              type="datetime-local"
              required
              value={form.submission_window_ends_at}
              onChange={(e) => onField('submission_window_ends_at', e.target.value)}
              disabled={disabled}
              className="w-full rounded border border-hap-border bg-hap-surface px-3 py-2 text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50"
            />
            <span className="text-xs text-hap-muted">Wall-clock time submissions stop being accepted.</span>
          </label>
          <label className="block space-y-2">
            <span className="text-sm font-medium">Discussion ends</span>
            <input
              type="datetime-local"
              required
              value={form.discussion_ends_at}
              onChange={(e) => onField('discussion_ends_at', e.target.value)}
              disabled={disabled}
              className="w-full rounded border border-hap-border bg-hap-surface px-3 py-2 text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50"
            />
            <span className="text-xs text-hap-muted">Wall-clock ceiling for the session. Defaults to start + 45 min.</span>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-2">
            <span className="text-sm font-medium">Timezone</span>
            <input
              type="text"
              required
              value={form.timezone}
              onChange={(e) => onField('timezone', e.target.value)}
              disabled={disabled}
              className="w-full rounded border border-hap-border bg-hap-surface px-3 py-2 text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50"
              placeholder="America/Denver"
            />
            <span className="text-xs text-hap-muted">IANA name. Defaults to this browser's zone.</span>
          </label>
          <label className="block space-y-2">
            <span className="text-sm font-medium">Max per group</span>
            <input
              type="number"
              required
              min="1"
              step="1"
              value={form.max_members_per_group}
              onChange={(e) => onField('max_members_per_group', e.target.value)}
              disabled={disabled}
              className="w-full rounded border border-hap-border bg-hap-surface px-3 py-2 text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50"
            />
          </label>
        </div>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.merge_check_enabled}
            onChange={(e) => onField('merge_check_enabled', e.target.checked)}
            disabled={disabled}
            className="h-4 w-4 rounded border-hap-border text-hap-accent focus:ring-hap-accent disabled:opacity-50"
          />
          <span className="text-sm font-medium">Merge-check enabled</span>
          <span className="text-xs text-hap-muted">
            (kill switch per ADR-026 — leave on for normal sessions)
          </span>
        </label>

        {error ? (
          <p role="alert" className="text-sm text-hap-danger">
            {error}
          </p>
        ) : null}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={disabled}
            className="flex-1 rounded bg-hap-accent px-4 py-2 font-medium text-white disabled:opacity-50"
          >
            {status === 'submitting' ? 'Creating…' : 'Create session'}
          </button>
          <Link
            to="/admin"
            className="self-center text-sm underline underline-offset-2 text-hap-muted hover:text-hap-ink"
          >
            Cancel
          </Link>
        </div>
      </form>
    </AdminFrame>
  )
}

function AdminCreateSuccess({ result, onCreateAnother }) {
  const [copied, setCopied] = useState(false)

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(result.organizer_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <AdminFrame title="Session created">
      <p className="text-hap-muted leading-relaxed">
        State: <code className="text-hap-ink">setup</code>. Share the organizer link with the facilitator.
      </p>

      <div className="space-y-2">
        <span className="text-xs uppercase tracking-widest text-hap-muted">Organizer link</span>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={result.organizer_url}
            className="flex-1 rounded border border-hap-border bg-hap-surface px-3 py-2 text-sm text-hap-ink font-mono"
          />
          <button
            type="button"
            onClick={onCopy}
            className="rounded bg-hap-accent px-4 py-2 font-medium text-white"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <span className="text-xs text-hap-muted">
          Anyone with this link has organizer access to this session.
        </span>
      </div>

      <div className="space-y-2 pt-2">
        <Link
          to={`/admin/sessions/${result.organizer_token}`}
          className="block rounded bg-hap-accent px-4 py-2 text-center font-medium text-white"
        >
          Manage this session
        </Link>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCreateAnother}
            className="flex-1 rounded border border-hap-border bg-hap-surface px-4 py-2 font-medium text-hap-ink hover:bg-hap-border"
          >
            Create another
          </button>
          <Link
            to="/admin"
            className="flex-1 rounded border border-hap-border bg-hap-surface px-4 py-2 text-center font-medium text-hap-ink hover:bg-hap-border"
          >
            Back to admin
          </Link>
        </div>
      </div>
    </AdminFrame>
  )
}

// ---------------------------------------------------------------------------
// /admin/sessions/:organizer_token — admin session detail (ADR-031 §Decision 6)
//   Behind AdminGuard. Loads the session by organizer_token (admin RLS on
//   sessions allows the SELECT), lists its active submissions, and exposes
//   two admin-only data operations:
//
//   - [Delete] per submission row → calls delete-submission edge fn (soft).
//     Frees the (session_id, email) slot for resubmit; preserves audit row.
//
//   - [Reset Session] → confirm dialog requiring the user to type the
//     session code → calls reset-session edge fn (HARD-deletes all
//     submissions + groups for this session; session row + config preserved).
//
//   The asymmetry is intentional per ADR-031 §Decision 6: per-row is
//   surgical and preserves audit; per-session is a clean-slate operation
//   for pre-event-day testing where audit doesn't matter.
//
//   Loading uses the anon-keyed supabase client + the admin's user JWT
//   (set by AdminGuard's earlier sign-in). Admin RLS policies cover the
//   reads. The two writes go through edge functions (delete-submission,
//   reset-session) which run their own in-body is_admin() check per
//   ADR-030, so even if the dashboard were temporarily reachable without
//   admin auth, the writes would be rejected.
// ---------------------------------------------------------------------------

function fmtDateTime(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function EndSessionConfirm({ session, pending, error, onCancel, onConfirm }) {
  const [typed, setTyped] = useState('')
  const matches = typed === session.code
  return (
    <div className="rounded border border-hap-danger bg-hap-surface p-4 space-y-3">
      <p className="text-sm font-medium text-hap-ink">End this session?</p>
      <p className="text-sm text-hap-muted leading-relaxed">
        Ending a session{' '}
        <strong className="text-hap-ink">stops all new submissions</strong>{' '}
        and transitions state to <code className="text-hap-ink">ended</code>. Existing submissions and groups are preserved.{' '}
        <strong className="text-hap-ink">You cannot re-open an ended session.</strong>{' '}
        To restart from scratch, create a new session (or end this one, then Reset Session to wipe its data).
      </p>
      <label className="block space-y-1">
        <span className="text-sm text-hap-ink">
          Type the session code{' '}
          <code className="rounded bg-hap-bg px-1 py-0.5 text-hap-ink">{session.code}</code>{' '}
          to confirm:
        </span>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={pending}
          autoFocus
          className="w-full rounded border border-hap-border bg-white px-3 py-2 font-mono text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-danger disabled:opacity-50"
        />
      </label>
      {error ? (
        <p role="alert" className="text-sm text-hap-danger">{error}</p>
      ) : null}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => onConfirm(typed)}
          disabled={!matches || pending}
          className="flex-1 rounded bg-hap-danger px-4 py-2 font-medium text-white disabled:opacity-50"
        >
          {pending ? 'Ending…' : 'End session'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="flex-1 rounded border border-hap-border bg-hap-surface px-4 py-2 font-medium text-hap-ink hover:bg-hap-border disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function ResetSessionConfirm({ session, pending, error, onCancel, onConfirm }) {
  const [typed, setTyped] = useState('')
  const matches = typed === session.code
  return (
    <div className="rounded border border-hap-danger bg-hap-surface p-4 space-y-3">
      <p className="text-sm font-medium text-hap-ink">Reset this session?</p>
      <p className="text-sm text-hap-muted leading-relaxed">
        This <strong className="text-hap-ink">hard-deletes every submission and every group</strong> in
        this session. The session row, code, and config (timer length,
        max-per-group, merge-check) are preserved. Audit history for this
        session is lost. <strong className="text-hap-ink">This cannot be undone.</strong>
      </p>
      <label className="block space-y-1">
        <span className="text-sm text-hap-ink">
          Type the session code{' '}
          <code className="rounded bg-hap-bg px-1 py-0.5 text-hap-ink">{session.code}</code>{' '}
          to confirm:
        </span>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={pending}
          autoFocus
          className="w-full rounded border border-hap-border bg-white px-3 py-2 font-mono text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-danger disabled:opacity-50"
        />
      </label>
      {error ? (
        <p role="alert" className="text-sm text-hap-danger">{error}</p>
      ) : null}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => onConfirm(typed)}
          disabled={!matches || pending}
          className="flex-1 rounded bg-hap-danger px-4 py-2 font-medium text-white disabled:opacity-50"
        >
          {pending ? 'Resetting…' : 'Reset session'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="flex-1 rounded border border-hap-border bg-hap-surface px-4 py-2 font-medium text-hap-ink hover:bg-hap-border disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function AdminSessionDetail() {
  const { organizer_token } = useParams()

  // loadState: 'loading' | 'loaded' | 'not_found' | 'error'
  const [loadState, setLoadState] = useState('loading')
  const [loadError, setLoadError] = useState(null)
  const [session, setSession] = useState(null)
  const [submissions, setSubmissions] = useState([])

  // Per-action state. deletingId holds the submission_id whose [Delete]
  // button is in flight (for spinner + disable). actionError surfaces a
  // non-blocking error message at the top of the list.
  const [deletingId, setDeletingId] = useState(null)
  const [actionError, setActionError] = useState(null)

  // Reset dialog state.
  const [showReset, setShowReset] = useState(false)
  const [resetPending, setResetPending] = useState(false)
  const [resetError, setResetError] = useState(null)
  const [resetSummary, setResetSummary] = useState(null)

  // State-transition dialog state (ADR-032: setup → live → ended).
  const [showEndConfirm, setShowEndConfirm] = useState(false)
  const [transitionPending, setTransitionPending] = useState(false)
  const [transitionError, setTransitionError] = useState(null)
  const [transitionSuccess, setTransitionSuccess] = useState(null)

  // Export state (ADR-033). Tracks whether an export is in flight and the
  // last-successful export filename/count so we can show a confirmation.
  const [exportPending, setExportPending] = useState(false)
  const [exportError, setExportError] = useState(null)
  const [exportInfo, setExportInfo] = useState(null) // { filename, submission_count, redacted_email_count }

  async function loadAll() {
    setActionError(null)
    setLoadError(null)
    // 1. Session by organizer_token. Admin RLS on sessions allows SELECT.
    const { data: sess, error: sessErr } = await supabase
      .from('sessions')
      .select('id, code, name, facilitator_name, state, start_time, submission_window_ends_at, discussion_ends_at, timezone, max_members_per_group, merge_check_enabled, organizer_token, created_at')
      .eq('organizer_token', organizer_token)
      .maybeSingle()
    if (sessErr) {
      setLoadState('error')
      setLoadError(sessErr.message)
      return
    }
    if (!sess) {
      setLoadState('not_found')
      return
    }
    setSession(sess)

    // 2. Active submissions for that session, joined to group name.
    const { data: subs, error: subsErr } = await supabase
      .from('submissions')
      .select('id, email, topic, created_at, group_id, classification_model, groups(name)')
      .eq('session_id', sess.id)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
    if (subsErr) {
      setLoadState('error')
      setLoadError(subsErr.message)
      return
    }
    setSubmissions(subs || [])
    setLoadState('loaded')
  }

  useEffect(() => {
    loadAll()
    // organizer_token is a route param; refetch if it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizer_token])

  async function handleDelete(submissionId) {
    if (!submissionId) return
    // Inline confirm — minimal UI; per-row consequences are small (audit
    // row is preserved by the soft-delete in the edge fn).
    if (!window.confirm('Delete this submission? The slot frees for resubmit; the audit row is preserved.')) {
      return
    }
    setDeletingId(submissionId)
    setActionError(null)
    try {
      const { data, error } = await supabase.functions.invoke('delete-submission', {
        body: { submission_id: submissionId },
      })
      if (error) {
        const env = await readErrorEnvelope(error)
        setActionError(`Delete failed: ${env?.error || error.message || 'unknown error'}`)
        setDeletingId(null)
        return
      }
      if (!data?.ok) {
        setActionError(`Delete failed: ${data?.error || 'unexpected response'}`)
        setDeletingId(null)
        return
      }
      // Refresh the list. The deleted row has deleted_at set so the
      // is('deleted_at', null) filter removes it on the next load.
      await loadAll()
    } catch (e) {
      setActionError(`Delete threw: ${String(e)}`)
    } finally {
      setDeletingId(null)
    }
  }

  async function handleResetConfirm(typedCode) {
    if (!session) return
    setResetPending(true)
    setResetError(null)
    try {
      const { data, error } = await supabase.functions.invoke('reset-session', {
        body: { session_id: session.id, confirm_code: typedCode },
      })
      if (error) {
        const env = await readErrorEnvelope(error)
        setResetError(env?.error === 'confirm_mismatch'
          ? 'The code you typed does not match the session code.'
          : `Reset failed: ${env?.error || error.message || 'unknown error'}`)
        setResetPending(false)
        return
      }
      if (!data?.ok) {
        setResetError(`Reset failed: ${data?.error || 'unexpected response'}`)
        setResetPending(false)
        return
      }
      setResetSummary({
        submissions_deleted: data.submissions_deleted ?? 0,
        groups_deleted: data.groups_deleted ?? 0,
      })
      setShowReset(false)
      setResetPending(false)
      await loadAll()
    } catch (e) {
      setResetError(`Reset threw: ${String(e)}`)
      setResetPending(false)
    }
  }

  // ---- State transitions (ADR-032) ----

  function friendlyTransitionError(env, fallback) {
    if (!env?.error) return fallback
    switch (env.error) {
      case 'another_session_live':
        return `Can't go live — another session is already live (${env.detail || 'unknown'}). End that session first.`
      case 'confirm_mismatch':
        return 'The code you typed does not match the session code.'
      case 'invalid_transition':
        return `Transition not allowed (${env.detail || ''}).`
      case 'not_admin':
        return 'Not authorized.'
      case 'session_not_found':
        return 'Session not found. It may have been removed.'
      default:
        return fallback
    }
  }

  async function handleGoLive() {
    if (!session) return
    if (!window.confirm('Go live now? Attendees will be able to join and submit topics immediately. You can end the session later.')) {
      return
    }
    setTransitionPending(true)
    setTransitionError(null)
    setTransitionSuccess(null)
    try {
      const { data, error } = await supabase.functions.invoke('set-session-state', {
        body: { session_id: session.id, to_state: 'live' },
      })
      if (error) {
        const env = await readErrorEnvelope(error)
        setTransitionError(friendlyTransitionError(env, `Go-live failed: ${env?.error || error.message || 'unknown error'}`))
        setTransitionPending(false)
        return
      }
      if (!data?.ok) {
        setTransitionError(`Go-live failed: ${data?.error || 'unexpected response'}`)
        setTransitionPending(false)
        return
      }
      setTransitionSuccess({ from_state: data.from_state, to_state: data.to_state })
      setTransitionPending(false)
      await loadAll()
    } catch (e) {
      setTransitionError(`Go-live threw: ${String(e)}`)
      setTransitionPending(false)
    }
  }

  async function handleEndConfirm(typedCode) {
    if (!session) return
    setTransitionPending(true)
    setTransitionError(null)
    try {
      const { data, error } = await supabase.functions.invoke('set-session-state', {
        body: { session_id: session.id, to_state: 'ended', confirm_code: typedCode },
      })
      if (error) {
        const env = await readErrorEnvelope(error)
        setTransitionError(friendlyTransitionError(env, `End failed: ${env?.error || error.message || 'unknown error'}`))
        setTransitionPending(false)
        return
      }
      if (!data?.ok) {
        setTransitionError(`End failed: ${data?.error || 'unexpected response'}`)
        setTransitionPending(false)
        return
      }
      setTransitionSuccess({ from_state: data.from_state, to_state: data.to_state })
      setShowEndConfirm(false)
      setTransitionPending(false)
      await loadAll()
      // ADR-033: auto-export on End. Fire-and-forget — if the export fails
      // we surface it in exportError but don't roll back the state change
      // (the row is already in session_exports via the edge-fn upsert, and
      // the admin can re-export from the button anyway).
      try {
        await handleExportSession()
      } catch {
        // handleExportSession sets its own error state; swallow here.
      }
    } catch (e) {
      setTransitionError(`End threw: ${String(e)}`)
      setTransitionPending(false)
    }
  }

  // ADR-033: per-session export. Calls export-session-data with this
  // session's id, then zips + downloads in the browser. Edge fn upserts
  // session_exports and stamps sessions.export_delivered_at as a side
  // effect (single-session mode only).
  async function handleExportSession() {
    if (!session) return
    setExportPending(true)
    setExportError(null)
    setExportInfo(null)
    try {
      const { data, error } = await supabase.functions.invoke('export-session-data', {
        body: { session_id: session.id },
      })
      if (error) {
        const env = await readErrorEnvelope(error)
        setExportError(`Export failed: ${env?.error || error.message || 'unknown error'}`)
        setExportPending(false)
        return
      }
      if (!data?.ok) {
        setExportError(`Export failed: ${data?.error || 'unexpected response'}`)
        setExportPending(false)
        return
      }
      const filename = await downloadExportZip(data)
      setExportInfo({
        filename,
        submission_count: data.meta?.submission_count ?? 0,
        redacted_email_count: data.meta?.redacted_email_count ?? 0,
      })
      setExportPending(false)
    } catch (e) {
      setExportError(`Export threw: ${String(e)}`)
      setExportPending(false)
    }
  }

  // ---- Render branches ----
  if (loadState === 'loading') {
    return (
      <AdminFrame title="Session">
        <p className="text-hap-muted" role="status" aria-live="polite">
          Loading session…
        </p>
      </AdminFrame>
    )
  }
  if (loadState === 'not_found') {
    return (
      <AdminFrame title="Session not found">
        <p className="text-hap-muted leading-relaxed">
          No session matches that organizer token. The token may be wrong, or
          the session may have been removed.
        </p>
        <Link to="/admin" className="text-sm underline underline-offset-2">
          Back to admin
        </Link>
      </AdminFrame>
    )
  }
  if (loadState === 'error') {
    return (
      <AdminFrame title="Session">
        <p className="text-hap-danger" role="alert">
          Could not load this session: {loadError}
        </p>
        <Link to="/admin" className="text-sm underline underline-offset-2">
          Back to admin
        </Link>
      </AdminFrame>
    )
  }

  // loaded
  return (
    <main className="min-h-screen bg-hap-bg text-hap-ink font-sans px-6 py-12">
      <div className="max-w-4xl mx-auto space-y-8">
        <header className="space-y-1">
          <p className="text-xs uppercase tracking-widest text-hap-muted">
            Human AI Partners · Admin
          </p>
          <h1 className="text-3xl font-semibold leading-tight">{session.name}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-sm text-hap-muted">
            <span>Code: <code className="text-hap-ink">{session.code}</code></span>
            <span>State: <code className="text-hap-ink">{session.state}</code></span>
            <span>Facilitator: <span className="text-hap-ink">{session.facilitator_name}</span></span>
            <span>Submit closes: <span className="text-hap-ink">{formatSessionTime(session.submission_window_ends_at, session.timezone)}</span></span>
            <span>Discussion ends: <span className="text-hap-ink">{formatSessionTime(session.discussion_ends_at, session.timezone)}</span></span>
            <span>Timezone: <span className="text-hap-ink">{session.timezone}</span></span>
            <span>Max per group: <span className="text-hap-ink">{session.max_members_per_group}</span></span>
            <span>Merge check: <span className="text-hap-ink">{session.merge_check_enabled ? 'on' : 'off'}</span></span>
          </div>
          <div className="pt-2 text-sm flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/admin" className="underline underline-offset-2 text-hap-muted hover:text-hap-ink">
              ← Back to admin
            </Link>
            <Link
              to={`/admin/sessions/${session.organizer_token}/config`}
              className="underline underline-offset-2 text-hap-muted hover:text-hap-ink"
            >
              Edit runtime config →
            </Link>
          </div>
        </header>

        {resetSummary ? (
          <div className="rounded border border-hap-border bg-hap-surface p-3">
            <p className="text-sm text-hap-ink">
              Session reset complete. Deleted{' '}
              <span className="font-medium">{resetSummary.submissions_deleted}</span>{' '}
              submission{resetSummary.submissions_deleted === 1 ? '' : 's'} and{' '}
              <span className="font-medium">{resetSummary.groups_deleted}</span>{' '}
              group{resetSummary.groups_deleted === 1 ? '' : 's'}. Session config preserved.
            </p>
          </div>
        ) : null}

        {transitionSuccess ? (
          <div className="rounded border border-hap-border bg-hap-surface p-3">
            <p className="text-sm text-hap-ink">
              State change applied:{' '}
              <code className="text-hap-ink">{transitionSuccess.from_state}</code>{' '}→{' '}
              <code className="text-hap-ink">{transitionSuccess.to_state}</code>.
              {transitionSuccess.to_state === 'live'
                ? ' Attendees can now join from the landing page.'
                : transitionSuccess.to_state === 'ended'
                  ? ' Submissions are closed for this session.'
                  : ''}
            </p>
          </div>
        ) : null}

        {actionError ? (
          <p role="alert" className="text-sm text-hap-danger">{actionError}</p>
        ) : null}

        {/* ADR-032: state transition controls. Above Submissions so they're
             the first action the admin sees on the page. */}
        <section className="space-y-3">
          {showEndConfirm ? (
            <EndSessionConfirm
              session={session}
              pending={transitionPending}
              error={transitionError}
              onCancel={() => { setShowEndConfirm(false); setTransitionError(null) }}
              onConfirm={handleEndConfirm}
            />
          ) : session.state === 'setup' ? (
            <div className="rounded border border-hap-border bg-hap-surface p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-hap-ink">Session is in setup.</p>
                <p className="text-sm text-hap-muted leading-relaxed">
                  Attendees can't join until you go live. Only one session can be live at a time.
                </p>
              </div>
              {transitionError ? (
                <p role="alert" className="text-sm text-hap-danger">{transitionError}</p>
              ) : null}
              <button
                type="button"
                onClick={handleGoLive}
                disabled={transitionPending}
                className="rounded bg-hap-accent px-4 py-2 font-medium text-white disabled:opacity-50"
              >
                {transitionPending ? 'Going live…' : 'Go live'}
              </button>
            </div>
          ) : session.state === 'live' ? (
            <div className="rounded border border-hap-border bg-hap-surface p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-hap-ink">Session is live.</p>
                <p className="text-sm text-hap-muted leading-relaxed">
                  Attendees can join from the landing page and submit topics. End the session to stop accepting submissions.
                </p>
              </div>
              {transitionError ? (
                <p role="alert" className="text-sm text-hap-danger">{transitionError}</p>
              ) : null}
              <button
                type="button"
                onClick={() => { setShowEndConfirm(true); setTransitionError(null); setTransitionSuccess(null) }}
                disabled={transitionPending}
                className="rounded border border-hap-danger px-4 py-2 font-medium text-hap-danger hover:bg-hap-danger hover:text-white disabled:opacity-50"
              >
                End session
              </button>
            </div>
          ) : session.state === 'ended' ? (
            <div className="rounded border border-hap-border bg-hap-surface p-4">
              <p className="text-sm text-hap-ink">
                This session has ended. No further state transitions are available.
              </p>
            </div>
          ) : null}
        </section>

        {/* ADR-033: export. Available at any state. No gate / no confirm —
             export is non-destructive. Non-opted-in emails are redacted in
             the bundle per ADR-014 retention policy. */}
        <section className="space-y-3">
          <div className="rounded border border-hap-border bg-hap-surface p-4 space-y-3">
            <div className="space-y-1">
              <p className="text-sm font-medium text-hap-ink">Export this session</p>
              <p className="text-sm text-hap-muted leading-relaxed">
                Downloads a .zip with submissions.csv, groups.csv, sessions.csv, and a JSON archive.
                Emails of attendees who did <span className="italic">not</span> opt in are blank in the export.
              </p>
            </div>
            {exportError ? (
              <p role="alert" className="text-sm text-hap-danger">{exportError}</p>
            ) : null}
            {exportInfo ? (
              <p role="status" aria-live="polite" className="text-sm text-hap-muted">
                Downloaded <code className="text-hap-ink">{exportInfo.filename}</code>
                {' '}({exportInfo.submission_count} submission{exportInfo.submission_count === 1 ? '' : 's'},
                {' '}{exportInfo.redacted_email_count} email{exportInfo.redacted_email_count === 1 ? '' : 's'} redacted).
              </p>
            ) : null}
            <button
              type="button"
              onClick={handleExportSession}
              disabled={exportPending}
              className="rounded border border-hap-border bg-white px-4 py-2 font-medium text-hap-ink hover:bg-hap-border disabled:opacity-50"
            >
              {exportPending ? 'Building export…' : 'Export session'}
            </button>
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-xl font-semibold">
              Submissions{' '}
              <span className="text-sm font-normal text-hap-muted">
                ({submissions.length} active)
              </span>
            </h2>
            <button
              type="button"
              onClick={loadAll}
              className="text-sm underline underline-offset-2 text-hap-muted hover:text-hap-ink"
            >
              Refresh
            </button>
          </div>

          {submissions.length === 0 ? (
            <p className="text-sm text-hap-muted italic">
              No active submissions yet.
            </p>
          ) : (
            <ul className="divide-y divide-hap-border rounded border border-hap-border bg-hap-surface">
              {submissions.map((s) => {
                const isDeleting = deletingId === s.id
                const groupName = s.groups?.name || (s.group_id ? '(unnamed)' : '— unclassified —')
                return (
                  <li key={s.id} className="p-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="flex-1 min-w-0 space-y-1">
                      <p className="text-sm">
                        <code className="text-hap-ink">{s.email}</code>
                      </p>
                      <p className="text-sm text-hap-muted break-words">
                        <span className="text-hap-ink">{s.topic}</span>
                      </p>
                      <p className="text-xs text-hap-muted">
                        Group: <span className="text-hap-ink">{groupName}</span>
                        {s.classification_model ? (
                          <span> · model: <code className="text-hap-ink">{s.classification_model}</code></span>
                        ) : null}
                        <span> · {fmtDateTime(s.created_at)}</span>
                      </p>
                    </div>
                    <div className="shrink-0">
                      <button
                        type="button"
                        onClick={() => handleDelete(s.id)}
                        disabled={isDeleting || deletingId !== null}
                        className="rounded border border-hap-danger px-3 py-1 text-sm font-medium text-hap-danger hover:bg-hap-danger hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <section className="space-y-3 pt-4 border-t border-hap-border">
          <h2 className="text-xl font-semibold text-hap-danger">Danger zone</h2>
          {showReset ? (
            <ResetSessionConfirm
              session={session}
              pending={resetPending}
              error={resetError}
              onCancel={() => { setShowReset(false); setResetError(null) }}
              onConfirm={handleResetConfirm}
            />
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-hap-muted leading-relaxed">
                Reset hard-deletes every submission and every group for this
                session. Useful for clean dry-runs before the live event. The
                session row, code, and config stay intact — same URL, same
                organizer link, same timer / max-per-group settings.
              </p>
              <button
                type="button"
                onClick={() => { setShowReset(true); setResetError(null); setResetSummary(null) }}
                className="rounded border border-hap-danger px-4 py-2 font-medium text-hap-danger hover:bg-hap-danger hover:text-white"
              >
                Reset session
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// /join — outer shell: resolve active session, then hand off to JoinFlow
// ---------------------------------------------------------------------------

function JoinLoading() {
  return (
    <JoinFrame>
      <p className="text-hap-muted" role="status" aria-live="polite">
        Checking for an active session…
      </p>
    </JoinFrame>
  )
}

function JoinError({ detail }) {
  return (
    <JoinFrame>
      <div className="space-y-2">
        <p className="text-hap-ink font-medium">Something went wrong.</p>
        <p className="text-sm text-hap-muted">
          The session lookup failed. Try refreshing. If this keeps happening, flag it to the facilitator.
        </p>
        {detail ? (
          <pre className="text-xs text-hap-muted whitespace-pre-wrap break-words pt-3 border-t border-hap-border">
            {detail}
          </pre>
        ) : null}
      </div>
    </JoinFrame>
  )
}

function JoinHolding({ nextAt, nextName }) {
  return (
    <JoinFrame>
      <div className="space-y-3">
        <p className="text-hap-ink font-medium">No session is active right now.</p>
        {nextAt ? (
          <p className="text-hap-muted">
            Next session{nextName ? `, “${nextName}”,` : ''} starts at{' '}
            <span className="text-hap-ink">{formatWhen(nextAt)}</span>. Come back then.
          </p>
        ) : (
          <p className="text-hap-muted">
            Check back around the session start time on the printed sign.
          </p>
        )}
      </div>
    </JoinFrame>
  )
}

function Join() {
  const [state, setState] = useState({ status: 'loading' })

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const { data, error } = await supabase.functions.invoke('resolve-active-session')
        if (cancelled) return
        if (error) {
          setState({ status: 'error', detail: error?.message || 'Unknown function error' })
          return
        }
        setState({ status: 'loaded', data })
      } catch (e) {
        if (cancelled) return
        setState({ status: 'error', detail: String(e) })
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  if (state.status === 'loading') return <JoinLoading />
  if (state.status === 'error') return <JoinError detail={state.detail} />

  const payload = state.data || {}
  if (payload.status === 'live' && payload.session) {
    return <JoinFlow session={payload.session} />
  }
  return <JoinHolding nextAt={payload.next_session_at} nextName={payload.next_session_name} />
}

// ---------------------------------------------------------------------------
// JoinFlow — state machine for form → submitting → opt-in → group
// ---------------------------------------------------------------------------
//
// step: 'form' → user is filling email + topic
// step: 'submitting' → submit-topic call in flight
// step: 'submit-error' → submit-topic returned ok:false or threw
// step: 'opt-in' → submit succeeded (not duplicate); waiting on Yes/No
// step: 'opting-in' → record-opt-in call in flight
// step: 'group' → final landing page (also reached directly when duplicate)
//
// Per ADR-002, the opt-in screen has two equal-weight buttons. "No" does
// not write anything (the row was inserted with opted_in_to_newsletter=false
// by default). "Yes" calls record-opt-in.
//
// Per ADR-016, both submit-topic and record-opt-in run service-role on the
// server; the client only sees the JWT-verified function endpoints.

// supabase-js v2 wraps non-2xx edge function responses in a FunctionsHttpError
// whose `.message` is the unhelpful string "Edge Function returned a non-2xx
// status code". The actual JSON envelope lives on `.context`, which is a
// fetch Response object that can be consumed once. Read it defensively —
// the body may have been consumed already, may not be JSON, or may be
// empty if the function 500'd before writing a response.
async function readErrorEnvelope(error) {
  const ctx = error && error.context
  if (!ctx || typeof ctx.json !== 'function') return null
  try {
    return await ctx.json()
  } catch {
    return null
  }
}

const TOPIC_MAX = 200
const FIRST_NAME_MAX = 24 // ADR-042; mirrors DB CHECK submissions_first_name_length (_017)

function JoinFlow({ session }) {
  // ADR-031: `intent` is undefined on a fresh first submission, and 'resubmit'
  // when the user has been routed back to the form via the [Change my topic]
  // button on GroupSkeleton. handleSubmit forwards it verbatim to submit-topic.
  const [state, setState] = useState({ step: 'form', email: '', firstName: '', topic: '', intent: undefined })

  function setField(field, value) {
    setState((s) => ({ ...s, [field]: value }))
  }

  // Called from GroupSkeleton's [Change my topic] button. Returns to the
  // form pre-filled with the existing email + topic; sets intent='resubmit'
  // so the next submit displaces the prior row instead of returning duplicate.
  function handleChangeTopic() {
    setState((s) => ({
      ...s,
      step: 'form',
      intent: 'resubmit',
      email: s.submission?.email || s.email,
      firstName: s.submission?.first_name || s.firstName || '',
      topic: s.submission?.topic || s.topic,
    }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (state.step !== 'form') return

    const email = state.email.trim()
    const topic = state.topic.trim()
    const firstName = (state.firstName || '').trim()
    if (!email || !topic) return
    // ADR-042 §Decision 1: 24-char post-trim cap. Mirrors edge-fn validation
    // and DB CHECK submissions_first_name_length. Edge fn will also reject
    // but the front-end gate avoids the round-trip on obvious overruns.
    if (firstName.length > FIRST_NAME_MAX) return

    const intent = state.intent
    setState((s) => ({ ...s, step: 'submitting' }))
    try {
      const body = { email, topic }
      if (firstName) body.first_name = firstName
      if (intent === 'resubmit') body.intent = 'resubmit'
      const { data, error } = await supabase.functions.invoke('submit-topic', {
        body,
      })
      if (error) {
        // supabase-js v2 FunctionsHttpError: 4xx/5xx response body is NOT
        // in `data`; it's on error.context (a Response object). Unwrap it
        // so the user sees the real error code (e.g. invalid_email,
        // rate_limited, no_live_session) instead of the generic
        // "Edge Function returned a non-2xx status code" message.
        const envelope = await readErrorEnvelope(error)
        setState((s) => ({
          ...s,
          step: 'submit-error',
          errorCode: envelope?.error || 'invoke_error',
          // If we got a valid envelope, trust it — its absent `detail` means
          // "no extra detail to show." Only fall back to error.message when
          // the envelope itself couldn't be parsed (network error, non-JSON
          // body, etc.), since error.message is the unhelpful
          // "Edge Function returned a non-2xx status code" string.
          errorDetail: envelope ? envelope.detail : (error?.message || 'Unknown invoke error'),
        }))
        return
      }
      if (!data?.ok) {
        setState((s) => ({
          ...s,
          step: 'submit-error',
          errorCode: data?.error || 'unknown_error',
          errorDetail: data?.detail,
        }))
        return
      }
      // ADR-023: no JWT install. submit-topic v6 returns
      //   { ok, submission_id, group_id, duplicate, resubmit, session_id }
      // The submission_id is the capability token; GroupSkeleton uses it
      // to call the get-my-group edge function on a 2 s poll.
      // ADR-031: when data.resubmit === true the response represents a
      // freshly inserted replacement row. The user has already opted in (or
      // out) on their original submission — we skip the opt-in screen and
      // go straight to GroupSkeleton with the new submission_id.
      const submission = {
        id: data.submission_id,
        group_id: data.group_id,
        session_id: data.session_id,
        email,
        first_name: firstName || null,
        topic,
        duplicate: !!data.duplicate,
        resubmit: !!data.resubmit,
      }
      const skipOptIn = !!data.duplicate || !!data.resubmit
      setState({
        step: skipOptIn ? 'group' : 'opt-in',
        email,
        firstName,
        topic,
        submission,
        intent: undefined, // cleared after successful submit so a future
                           // [Change my topic] click is what re-arms it
      })
    } catch (err) {
      setState((s) => ({
        ...s,
        step: 'submit-error',
        errorCode: 'unexpected',
        errorDetail: String(err),
      }))
    }
  }

  async function handleOptIn(choice) {
    if (state.step !== 'opt-in' || !state.submission) return
    if (choice === 'no') {
      setState((s) => ({ ...s, step: 'group' }))
      return
    }
    setState((s) => ({ ...s, step: 'opting-in' }))
    try {
      const { data, error } = await supabase.functions.invoke('record-opt-in', {
        body: { submission_id: state.submission.id },
      })
      if (error || !data?.ok) {
        // Soft-fail: still advance to group screen. The opt-in row stays
        // false. Surface a non-blocking notice rather than trapping the
        // user on the opt-in screen.
        const envelope = error ? await readErrorEnvelope(error) : null
        // eslint-disable-next-line no-console
        console.warn(
          'record-opt-in failed:',
          envelope?.error || envelope?.detail || error?.message || data?.error,
        )
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('record-opt-in threw:', String(err))
    }
    setState((s) => ({
      ...s,
      step: 'group',
      submission: { ...s.submission, opted_in: true },
    }))
  }

  // ---- Render branches ----
  if (state.step === 'form' || state.step === 'submitting') {
    return (
      <SubmissionForm
        session={session}
        email={state.email}
        firstName={state.firstName || ''}
        topic={state.topic}
        onChangeField={setField}
        onSubmit={handleSubmit}
        submitting={state.step === 'submitting'}
        isResubmit={state.intent === 'resubmit'}
      />
    )
  }
  if (state.step === 'submit-error') {
    return (
      <SubmitErrorPanel
        errorCode={state.errorCode}
        errorDetail={state.errorDetail}
        onRetry={() => setState((s) => ({ ...s, step: 'form' }))}
      />
    )
  }
  if (state.step === 'opt-in' || state.step === 'opting-in') {
    return (
      <OptInPanel
        onChoose={handleOptIn}
        pending={state.step === 'opting-in'}
      />
    )
  }
  if (state.step === 'group') {
    return (
      <GroupSkeleton
        submission={state.submission}
        session={session}
        onChangeTopic={handleChangeTopic}
      />
    )
  }
  return null
}

// ---- Subcomponents -------------------------------------------------------

function SubmissionForm({ session, email, firstName, topic, onChangeField, onSubmit, submitting, isResubmit }) {
  const remaining = TOPIC_MAX - topic.length
  const tooLong = remaining < 0
  const firstNameTrimmed = (firstName || '').trim()
  const firstNameTooLong = firstNameTrimmed.length > FIRST_NAME_MAX
  const canSubmit = !submitting && email.trim() && topic.trim() && !tooLong && !firstNameTooLong

  return (
    <JoinFrame>
      <div className="space-y-2">
        <p className="text-base text-hap-muted">
          {isResubmit
            ? 'Change your topic'
            : (session?.name ? session.name : 'Join the active session')}
        </p>
        {isResubmit ? (
          <p className="text-base text-hap-muted">
            Edit your topic below. Submitting replaces your previous topic; we&rsquo;ll re-cluster you into a group.
          </p>
        ) : (
          <p className="text-2xl font-bold text-hap-ink leading-tight">
            What&rsquo;s on your mind today? Let&rsquo;s Chat!
          </p>
        )}
      </div>

      <form onSubmit={onSubmit} className="space-y-4 pt-2" noValidate>
        <label className="block space-y-1">
          <span className="block text-base text-hap-muted">Email</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            disabled={submitting}
            value={email}
            onChange={(e) => onChangeField('email', e.target.value)}
            placeholder="you@example.com"
            className="w-full bg-white border border-hap-border rounded-md px-3 py-2 text-base text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-ink"
          />
        </label>

        <label className="block space-y-1">
          <span className="flex justify-between text-base">
            <span className="text-hap-muted">Your name (optional)</span>
            <span className={firstNameTooLong ? 'text-red-600' : 'text-hap-muted'} aria-live="polite">
              {Math.max(FIRST_NAME_MAX - firstNameTrimmed.length, 0)} chars left
            </span>
          </span>
          <input
            type="text"
            autoComplete="given-name"
            disabled={submitting}
            value={firstName || ''}
            onChange={(e) => onChangeField('firstName', e.target.value)}
            maxLength={FIRST_NAME_MAX + 10 /* allow paste-then-trim */}
            placeholder="Friend"
            className="w-full bg-white border border-hap-border rounded-md px-3 py-2 text-base text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-ink"
          />
          <p className="text-sm text-hap-muted">Will be displayed on screen.</p>
        </label>

        <label className="block space-y-1">
          <span className="flex justify-between text-base">
            <span className="text-hap-muted">Topic</span>
            <span className={tooLong ? 'text-red-600' : 'text-hap-muted'} aria-live="polite">
              {Math.max(remaining, 0)} chars left
            </span>
          </span>
          <textarea
            required
            disabled={submitting}
            value={topic}
            onChange={(e) => onChangeField('topic', e.target.value)}
            rows={3}
            maxLength={TOPIC_MAX + 50 /* allow paste-then-trim */}
            placeholder="A short phrase or question — e.g. “Vibe coding” or “AI Tools”"
            className="w-full bg-white border border-hap-border rounded-md px-3 py-2 text-base text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-ink resize-y"
          />
        </label>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full bg-hap-ink text-white rounded-md py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {submitting
            ? (isResubmit ? 'Reclassifying…' : 'Submitting…')
            : (isResubmit ? 'Update topic' : 'Submit')}
        </button>

        <p className="text-sm text-hap-muted pt-2 border-t border-hap-border">
          Your email is visible only to you, the facilitator, and Human AI
          Partners admin &mdash; it is never displayed in the room. Your
          name, if given, shows on the projection so people in your group
          can find each other; leave it blank to appear as
          &ldquo;Friend&rdquo;. You may receive future communication from
          Human AI Partners if you opted in.
        </p>
      </form>
    </JoinFrame>
  )
}

function SubmitErrorPanel({ errorCode, errorDetail, onRetry }) {
  // Friendly copy per error code; falls back to raw detail.
  const COPY = {
    invalid_email: 'That email address doesn’t look right. Please check and try again.',
    email_required: 'Email is required.',
    topic_required: 'Topic is required.',
    topic_too_long: 'Topic is too long. Please trim to 200 characters or fewer.',
    first_name_too_long: 'First name is too long. Please trim to 24 characters or fewer.',
    invalid_intent: 'The app sent an unexpected request. Refresh the page and try again — if this keeps happening, flag it to the facilitator.',
    rate_limited: 'You’ve sent a few submissions in the last minute. Please wait a moment and try again.',
    no_live_session: 'The session ended while you were filling this out. Refresh to check for the next one.',
    db_error: 'We couldn’t save your submission. Please try again or flag this to the facilitator.',
    server_misconfigured: 'The server isn’t configured correctly. Please flag this to the facilitator.',
    invoke_error: 'We couldn’t reach the server. Check your connection and try again.',
    unknown_error: 'Something went wrong. Please try again.',
    unexpected: 'Something went wrong. Please try again.',
  }
  const message = COPY[errorCode] || COPY.unknown_error

  return (
    <JoinFrame>
      <div className="space-y-3">
        <p className="text-hap-ink font-medium">Submission didn’t go through.</p>
        <p className="text-sm text-hap-muted">{message}</p>
        {errorDetail ? (
          <pre className="text-xs text-hap-muted whitespace-pre-wrap break-words pt-2 border-t border-hap-border">
            {errorCode}: {errorDetail}
          </pre>
        ) : null}
        <button
          type="button"
          onClick={onRetry}
          className="bg-hap-ink text-white rounded-md py-2 px-4 font-medium"
        >
          Try again
        </button>
      </div>
    </JoinFrame>
  )
}

function OptInPanel({ onChoose, pending }) {
  // ADR-002: two equal-weight buttons, neither pre-selected. "Yes" and
  // "No" sit side-by-side at the same visual prominence. No pre-check,
  // no dark pattern.
  return (
    <JoinFrame>
      <div className="space-y-4">
        <p className="text-hap-ink font-medium">Got your topic.</p>
        <p className="text-hap-muted leading-relaxed">
          Want occasional updates from Human AI Partners about AI events
          and resources?
        </p>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <button
            type="button"
            onClick={() => onChoose('yes')}
            disabled={pending}
            className="bg-hap-ink text-white rounded-md py-2 font-medium disabled:opacity-50"
          >
            {pending ? 'Saving…' : 'Yes, add me'}
          </button>
          <button
            type="button"
            onClick={() => onChoose('no')}
            disabled={pending}
            className="bg-white text-hap-ink border border-hap-ink rounded-md py-2 font-medium disabled:opacity-50"
          >
            No thanks
          </button>
        </div>
        <p className="text-xs text-hap-muted pt-2 border-t border-hap-border">
          Either choice advances. We’ll never email you without your
          opt-in here.
        </p>
      </div>
    </JoinFrame>
  )
}

// ---------------------------------------------------------------------------
// GroupSkeleton — Session 14 (ADR-023) edge-function polling
// ---------------------------------------------------------------------------
//
// Poll get-my-group every 2 s with the submission_id. Edge function returns
//   { group_id, group_name, group_name_version, members: [{id,email,topic}] }
// or the empty shape { group_id: null, ..., members: [] } until the
// classifier places the submission.
//
// Replaces (Session 13 v6):
//   - direct SELECT on `groups` + realtime subscription on UPDATE
//   - direct SELECT on `submissions` + realtime '*' subscription
//   - own-row UPDATE realtime subscription + 5 s fallback poll
//
// The 2 s poll is the only mechanism. Polling is restartable, debuggable,
// and survives realtime channel failures. The edge function does the
// reads under the service role; the browser holds no auth state.
//
// The poll runs while the tab is in the foreground. document.hidden is
// checked at every tick — we skip the network call when the tab is hidden
// to avoid running thousands of tabs at full poll rate during the event.
// On re-show, the poll resumes on its next 2 s tick (no immediate catch-up
// fetch — the worst case is a 2 s gap, which is the same SLA as the
// foreground case).

const POLL_INTERVAL_MS = 2000

// Live countdown to discussion_ends_at. Ticks every 1s against Date.now()
// on the viewer's device — ADR-044 §Decision 4 (client drift ≤1s
// acceptable; clients do NOT decide transitions). Formats as "M:SS" when
// under an hour, "H:MM:SS" otherwise. Shows "—" while endsAt is null (setup
// state) and "0:00" once the countdown expires (the server decides the
// actual transition).
function DiscussionCountdown({ endsAt, timezone }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!endsAt) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [endsAt])

  if (!endsAt) {
    return <span className="text-hap-muted">— (not scheduled)</span>
  }
  const target = new Date(endsAt).getTime()
  const remainingSec = Math.max(0, Math.floor((target - now) / 1000))
  const hours = Math.floor(remainingSec / 3600)
  const minutes = Math.floor((remainingSec % 3600) / 60)
  const seconds = remainingSec % 60
  const pad = (n) => String(n).padStart(2, '0')
  const formatted = hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`
  return (
    <>
      <span>{formatted}</span>
      <span className="text-hap-muted"> (ends {formatSessionTime(endsAt, timezone)})</span>
    </>
  )
}

function GroupSkeleton({ submission, session, onChangeTopic }) {
  const submissionId = submission?.id || null

  // groupState is the full shape returned by get-my-group, plus a small
  // local addition: loadError holds the last poll error message (if any)
  // so the UI can surface a soft warning without unmounting.
  //
  // ADR-023 §S38 amendment — `session_state` mirrors `sessions.state` from
  // get-my-group, used to gate the end-of-event UI (banner + disabled
  // Switch Groups + hidden Submit a different topic). Initialized from the
  // session prop loaded once at mount and updated on each 2 s poll, so the
  // phone reflects state transitions (live → ended) without page refresh.
  // Defaults to 'live' on missing/null so the phone never falsely renders
  // ended state.
  const [groupState, setGroupState] = useState({
    group_id: submission?.group_id || null,
    group_name: null,
    group_name_version: null,
    members: [],
    session_groups: [],
    session_state: session?.state || 'live',
  })
  const [loadError, setLoadError] = useState(null)

  // Picker state (ADR-027 + ADR-019 §2 + ADR-023 polling semantics).
  // pendingGroupId holds the target of an in-flight / server-acknowledged
  // switch until the next get-my-group poll reconciles group_id. No
  // optimistic update — the poll is the source of truth.
  // rowError holds the last inline error for a specific target row
  // (target_archived is the only user-actionable code post-ADR-028).
  const [pendingGroupId, setPendingGroupId] = useState(null)
  const [rowError, setRowError] = useState(null)

  // §Bug-1 fix (S23): the "Topic updated. We'll re-cluster you in a moment."
  // banner (rendered below when submission.resubmit is true) used to persist
  // indefinitely because the 2 s group poll never touches the resubmit flag
  // and the attendee stays on this page after a resubmit. We auto-dismiss
  // after 5 s of display time. Visibility is keyed on submission.id so a
  // second resubmit (new row, new id) reshows the banner for another 5 s.
  const [resubmitBannerVisible, setResubmitBannerVisible] = useState(true)
  useEffect(() => {
    if (!submission?.resubmit) return
    setResubmitBannerVisible(true)
    const t = setTimeout(() => setResubmitBannerVisible(false), 5000)
    return () => clearTimeout(t)
  }, [submissionId, submission?.resubmit])

  useEffect(() => {
    if (!submissionId) return
    let cancelled = false
    let timer = null

    async function pollOnce() {
      if (cancelled) return
      // Skip the call when the tab is hidden — no point fetching for a
      // user who isn't looking. The next visible tick picks it up.
      if (typeof document !== 'undefined' && document.hidden) {
        timer = setTimeout(pollOnce, POLL_INTERVAL_MS)
        return
      }
      try {
        const { data, error } = await supabase.functions.invoke('get-my-group', {
          body: { submission_id: submissionId },
        })
        if (cancelled) return
        if (error) {
          const envelope = await readErrorEnvelope(error)
          setLoadError(envelope?.error || error?.message || 'poll_error')
        } else if (data) {
          // The function returns the canonical shape; replace state
          // wholesale. Members order is stable (server orders by
          // created_at), so React reconciliation by key=id stays calm.
          setGroupState({
            group_id: data.group_id ?? null,
            group_name: data.group_name ?? null,
            group_name_version: data.group_name_version ?? null,
            members: Array.isArray(data.members) ? data.members : [],
            session_groups: Array.isArray(data.session_groups)
              ? data.session_groups
              : [],
            // ADR-023 §S38 — default to 'live' when field absent (older
            // edge fn) so phone never falsely shows ended state.
            session_state: data.session_state || 'live',
          })
          // Clear stale error after a successful poll.
          setLoadError(null)
        }
      } catch (e) {
        if (!cancelled) setLoadError(String(e))
      }
      if (!cancelled) {
        timer = setTimeout(pollOnce, POLL_INTERVAL_MS)
      }
    }

    // Kick off immediately on mount; subsequent polls re-arm in pollOnce.
    pollOnce()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [submissionId])

  // Reconcile pending switch once the poll reports group_id == target.
  // ADR-023 polling semantics: the POST triggers server state, but the
  // UI only clears pending state when the 2 s poll confirms it.
  useEffect(() => {
    if (pendingGroupId && groupState.group_id === pendingGroupId) {
      setPendingGroupId(null)
      setRowError(null)
    }
  }, [groupState.group_id, pendingGroupId])

  async function handleSwitch(targetGroupId) {
    if (!targetGroupId) return
    if (targetGroupId === groupState.group_id) return // suppress same_group client-side
    if (pendingGroupId) return // one switch in flight at a time
    const sessionId = session?.id
    const email = submission?.email
    if (!sessionId || !email) return // missing identity; should not happen on this screen

    setPendingGroupId(targetGroupId)
    setRowError(null)
    try {
      const { error } = await supabase.functions.invoke('switch-group', {
        body: { session_id: sessionId, email, target_group_id: targetGroupId },
      })
      if (error) {
        const envelope = await readErrorEnvelope(error)
        const code = envelope?.error
        if (code === 'target_archived') {
          setRowError({ group_id: targetGroupId, message: 'Group is no longer available' })
        } else {
          // not_attendee, session_not_live, invalid_*, db_error — user
          // can't act on these; log and fail silent per handoff.
          // eslint-disable-next-line no-console
          console.warn('switch-group error:', code || error?.message)
        }
        setPendingGroupId(null)
      }
      // On success, do NOT clear pendingGroupId here — wait for the
      // reconciliation effect above to clear it when the poll catches up.
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('switch-group exception:', String(e))
      setPendingGroupId(null)
    }
  }

  // ---- Render --------------------------------------------------------

  // ADR-015 §S38 — own-row identifier on the attendee surface uses the
  // user's first_name when given, else 'Friend' (without number) until
  // the next get-my-group poll attaches a real friend_number from the
  // session-wide map. The bare 'Friend' state is brief (~2s polling
  // window before classification places the user); it then becomes
  // FriendN matching the projection.
  const youLabel = (submission?.first_name || '').trim() || 'Friend'
  const hasGroup = !!groupState.group_id
  // ADR-023 §S38 — end-of-event UI gate. Reflects the live session state
  // from the 2 s poll. When true: banner renders above identity card,
  // Switch Groups buttons disabled, Submit-a-different-topic hidden,
  // Time Left swaps to "Discussion ended".
  const isEnded = groupState.session_state === 'ended'

  // Picker rows: all non-archived groups from session_groups. ADR-025 §4
  // filters archived client-side; ADR-027 §2 locks row content to name +
  // member_count (no topic samples, no Full badge).
  const pickerGroups = (groupState.session_groups || []).filter(
    (g) => g && g.archived_at === null,
  )

  // ADR-036 §S35 amendment Block 5: cluster-color reveal on the user's
  // identity card. Colors are derived via the same ADR-038 golden-angle
  // HSL function the projection uses, applied over the full card
  // background (NOT header-accent-only — Julie 2026-04-26). Foreground
  // text picked per WCAG luminance via pickTextColor().
  //
  // Pre-classification (group_id null): stay neutral, no shimmer (per
  // §S35 amendment — the color appears as a discrete reveal moment
  // when group_id flips non-null, not as an animated placeholder).
  //
  // Backend dependency: get-my-group's session_groups[] payload must
  // include `created_at` so root clusters sort identically to the
  // projection's ordering — landed in the S35 prereq edit to
  // supabase/functions/get-my-group/index.ts.
  //
  // Switch-group: when the user moves to a different group, polling
  // updates groupState.group_id and the memo recomputes — color
  // reveal switches naturally.
  // Compute colorMap once per session_groups change. Used by both the user's
  // "Your group" identity card AND the switch-group picker rows below — same
  // golden-angle hues across both surfaces, matching the projection screen.
  const colorMap = useMemo(() => {
    const sessionGroups = groupState.session_groups || []
    if (sessionGroups.length === 0) return null
    return computeClusterColors(sessionGroups)
  }, [groupState.session_groups])

  const userClusterStyle = useMemo(() => {
    if (!hasGroup || !colorMap) return null
    const color = colorMap.get(groupState.group_id)
    if (!color) return null
    return {
      backgroundColor: hslString(color),
      color: pickTextColor(color),
    }
  }, [hasGroup, groupState.group_id, colorMap])

  // Per-row style for the switch-group picker. Same color logic as
  // userClusterStyle but indexed by any group_id (not just the user's).
  const pickerStyleFor = (groupId) => {
    if (!colorMap) return null
    const color = colorMap.get(groupId)
    if (!color) return null
    return {
      backgroundColor: hslString(color),
      color: pickTextColor(color),
    }
  }

  // Sort members: user first, then by id (server already returned them
  // in created_at order, but we re-pin the user to the top of the list).
  //
  // ADR-015 §S38 — each member's `displayName` is computed from
  // `first_name` (trimmed) when given, else `Friend{friend_number}`
  // where friend_number is the server-assigned session-wide stable ID.
  // Pre-classification fallback (no members yet) renders the user as
  // their own first_name or bare 'Friend' until the poll attaches the
  // real friend_number.
  const displayMembers = (() => {
    if (!hasGroup) return []
    if (groupState.members.length === 0 && submission) {
      const trimmed = (submission.first_name || '').trim()
      return [{
        id: submission.id,
        topic: submission.topic,
        displayName: trimmed || 'Friend',
        isYou: true,
      }]
    }
    return groupState.members
      .map((m) => {
        const trimmed = (m.first_name || '').trim()
        const displayName = trimmed
          ? trimmed
          : (m.friend_number != null ? `Friend${m.friend_number}` : 'Friend')
        return {
          id: m.id,
          topic: m.topic,
          displayName,
          isYou: m.id === submissionId,
        }
      })
      .sort((a, b) => {
        if (a.isYou) return -1
        if (b.isYou) return 1
        return 0 // preserve server order otherwise
      })
  })()

  return (
    <JoinFrame>
      <div className="space-y-4">
        {/* ADR-023 §S38 — end-of-event banner. Renders above all other
            content when session.state === 'ended'. Identity card + members
            list stay visible below so attendees can still see who was in
            their group for in-room follow-up. */}
        {isEnded ? (
          <div className="rounded border border-hap-border bg-hap-surface p-4">
            <p className="text-lg font-semibold text-hap-ink">
              Session ended. Thanks for joining!
            </p>
          </div>
        ) : null}

        {submission?.duplicate ? (
          // ADR-031 §Decision 5: explicit duplicate card replaces the prior
          // soft "you're already in this session" message. Shows email +
          // previous topic + group so the user knows exactly what record
          // we're matching them against. The [Change my topic] affordance
          // at the bottom of the card lets them displace it.
          <div className="rounded border border-hap-border bg-hap-surface p-3 space-y-1">
            <p className="text-base text-hap-ink">
              You already submitted with{' '}
              <span className="font-medium">{submission.email}</span>.
            </p>
            {submission.topic ? (
              <p className="text-base text-hap-muted">
                Topic: <span className="italic text-hap-ink">{submission.topic}</span>
              </p>
            ) : null}
            {hasGroup && groupState.group_name ? (
              <p className="text-base text-hap-muted">
                Group: <span className="font-medium text-hap-ink">{groupState.group_name}</span>
              </p>
            ) : null}
          </div>
        ) : submission?.resubmit && resubmitBannerVisible ? (
          // Brief confirmation banner after a successful resubmit. Auto-
          // dismisses after 5 s (see resubmitBannerVisible effect above) so
          // the "in a moment" copy doesn't linger after re-cluster completes.
          // The explicit duplicate card does not render because
          // submission.duplicate is false on a 6c branch.
          <div className="rounded border border-hap-border bg-hap-surface p-3">
            <p className="text-base text-hap-ink">
              Topic updated. You’ve been moved to a new group.
            </p>
          </div>
        ) : null}

        {/* ADR-036 §S35 amendment Block 5: identity card. When a cluster
            color is available (post-classification), the card surface
            renders in that color with WCAG-luminance-picked foreground
            text. Pre-classification, the card stays in default surface
            styling — the color appears as a discrete reveal moment when
            group_id flips non-null. The "Your group" + "Members" sections
            are unified into a single colored card here because both
            describe the user's group identity; the timer + switch picker
            below stay neutral because they're about navigation, not
            identity. */}
        <div
          className={`rounded space-y-3 ${userClusterStyle ? 'p-4' : ''}`}
          style={userClusterStyle || undefined}
        >
          <div className="space-y-1">
            <p
              className={`text-sm uppercase tracking-widest ${userClusterStyle ? 'opacity-80' : 'text-hap-muted'}`}
            >
              Your group
            </p>
            <p className="text-2xl font-semibold" aria-live="polite">
              {hasGroup
                ? (groupState.group_name || 'Loading group name…')
                : 'Clustering your topic…'}
            </p>
          </div>

          <div
            className={`space-y-2 pt-3 ${
              userClusterStyle
                ? 'border-t border-current/30'
                : 'border-t border-hap-border'
            }`}
          >
            <p
              className={`text-sm uppercase tracking-widest ${userClusterStyle ? 'opacity-80' : 'text-hap-muted'}`}
            >
              Members{hasGroup && displayMembers.length > 0 ? ` (${displayMembers.length})` : ''}
            </p>
            <ul className="text-base grid grid-cols-2 gap-x-3 gap-y-1" aria-live="polite">
              {hasGroup ? (
                displayMembers.map((m) => (
                  <li key={m.id} className="truncate">
                    {m.displayName}
                    {m.isYou ? (
                      <span className={userClusterStyle ? 'opacity-75' : 'text-hap-muted'}>
                        {' '}
                        (you)
                      </span>
                    ) : null}
                  </li>
                ))
              ) : (
                <>
                  <li className="text-hap-ink">
                    {youLabel} <span className="text-hap-muted">(you)</span>
                  </li>
                  <li className="text-hap-muted italic">Waiting for the classifier…</li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t border-hap-border">
          <p className="text-sm uppercase tracking-widest text-hap-muted">Time left</p>
          <p className="text-base text-hap-ink">
            {isEnded ? (
              <span className="text-hap-muted">Discussion ended</span>
            ) : (
              <DiscussionCountdown
                endsAt={session?.discussion_ends_at}
                timezone={session?.timezone}
              />
            )}
          </p>
        </div>

        <div className="space-y-2 pt-2 border-t border-hap-border">
          <p className="text-sm uppercase tracking-widest text-hap-muted">
            Switch groups
          </p>
          {pickerGroups.length === 0 ? (
            <p className="text-sm text-hap-muted italic">
              No groups to show yet.
            </p>
          ) : (
            <ul className="space-y-2" aria-live="polite">
              {pickerGroups.map((g) => {
                const isCurrent = g.id === groupState.group_id
                const isPending = g.id === pendingGroupId
                // ADR-023 §S38 — once session is ended, all picker rows are
                // disabled (including the user's current row). Members list
                // + colors stay visible so attendees can see who was in
                // which group for in-room follow-up; only interactivity
                // is removed.
                const isDisabled = isCurrent || pendingGroupId !== null || isEnded
                const showRowError = rowError?.group_id === g.id
                const colorStyle = pickerStyleFor(g.id)
                return (
                  <li key={g.id}>
                    <button
                      type="button"
                      onClick={() => handleSwitch(g.id)}
                      disabled={isDisabled}
                      aria-current={isCurrent ? 'true' : undefined}
                      style={colorStyle || undefined}
                      className={`w-full text-left flex items-center justify-between gap-3 px-3 py-3 rounded text-base font-medium transition-opacity ${
                        colorStyle
                          ? (isCurrent
                              ? 'cursor-default ring-2 ring-hap-ink ring-offset-2 ring-offset-hap-bg'
                              : isPending
                                ? 'opacity-60 cursor-not-allowed'
                                : 'hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed')
                          : (isCurrent
                              ? 'border border-hap-border bg-hap-bg text-hap-ink cursor-default'
                              : 'border border-hap-border text-hap-ink hover:bg-hap-border/40 disabled:opacity-60 disabled:cursor-not-allowed')
                      }`}
                    >
                      <span className="flex-1 truncate">{g.name}</span>
                      <span className={`text-sm shrink-0 ${colorStyle ? 'opacity-80' : 'text-hap-muted'}`}>
                        {g.member_count}{' '}
                        {g.member_count === 1 ? 'person' : 'people'}
                      </span>
                      {isCurrent ? (
                        <span className={`text-xs uppercase tracking-widest shrink-0 ${colorStyle ? 'opacity-90' : 'text-hap-muted'}`}>
                          Current
                        </span>
                      ) : isPending ? (
                        <span className={`text-sm shrink-0 ${colorStyle ? 'opacity-80' : 'text-hap-muted'}`}>
                          Switching…
                        </span>
                      ) : null}
                    </button>
                    {showRowError ? (
                      <p className="text-sm text-red-600 pl-3 pt-1">
                        {rowError.message}
                      </p>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {loadError ? (
          <p className="text-sm text-red-600 pt-2 border-t border-hap-border">
            {loadError}
          </p>
        ) : null}

        {/*
          ADR-031 §Decision 5: [Change my topic] is exposed for everyone on
          this screen (duplicate path AND first-time path), so an attendee
          who realises mid-discussion that they want to switch topics has
          the same recourse as someone who returns to the form later. The
          button calls back into JoinFlow.handleChangeTopic which routes
          them to the form with intent='resubmit' set.
        */}
        {/* ADR-023 §S38 — hide Submit-a-different-topic when session ended.
            Resubmission no longer makes sense post-event; the affordance
            would imply the room is still gathering input. */}
        {onChangeTopic && !isEnded ? (
          <div className="pt-2 border-t border-hap-border">
            <button
              type="button"
              onClick={onChangeTopic}
              className="text-sm underline underline-offset-2 text-hap-muted hover:text-hap-ink"
            >
              Submit a different topic
            </button>
          </div>
        ) : null}

        <div className="text-base text-hap-ink pt-3 border-t border-hap-border text-center space-y-1">
          <p>
            Built by Human <span style={{ color: '#0891b2' }} className="font-semibold">AI</span> Partners
          </p>
          <p>
            <a
              href="https://humanaipartners.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 text-hap-ink hover:opacity-80"
            >
              humanaipartners.ai
            </a>
          </p>
        </div>
      </div>
    </JoinFrame>
  )
}

// ---------------------------------------------------------------------------
// /_probe (unchanged from Session 5)
// ---------------------------------------------------------------------------

function SupabaseProbe() {
  const [result, setResult] = useState({ status: 'running' })

  useEffect(() => {
    let cancelled = false

    async function run() {
      const t0 = performance.now()
      const { count, error } = await supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true })
      const latency_ms = Math.round(performance.now() - t0)
      if (cancelled) return
      if (error) {
        setResult({ status: 'error', detail: error.message, latency_ms })
      } else {
        setResult({ status: 'ok', count: count ?? 0, latency_ms })
      }
    }

    run()
    return () => { cancelled = true }
  }, [])

  return (
    <main className="min-h-screen bg-hap-bg text-hap-ink font-sans flex items-center justify-center px-6">
      <div className="max-w-lg w-full space-y-3">
        <p className="text-xs uppercase tracking-widest text-hap-muted">Phase 1 probe</p>
        <h1 className="text-xl font-semibold">Supabase connection</h1>
        {result.status === 'running' && (
          <p className="text-sm text-hap-muted">Running count query against sessions…</p>
        )}
        {result.status === 'ok' && (
          <div className="text-sm space-y-1">
            <p><span className="text-hap-muted">status:</span> ok</p>
            <p><span className="text-hap-muted">sessions count:</span> {result.count}</p>
            <p><span className="text-hap-muted">latency:</span> {result.latency_ms}ms</p>
            <p className="pt-3 border-t border-hap-border text-xs text-hap-muted">
              RLS is fail-closed for anon. An empty DB plus a restrictive policy both produce
              count: 0 without error. What this probe confirms is the client reached Supabase.
            </p>
          </div>
        )}
        {result.status === 'error' && (
          <div className="text-sm space-y-1">
            <p><span className="text-hap-muted">status:</span> error</p>
            <p><span className="text-hap-muted">latency:</span> {result.latency_ms}ms</p>
            <pre className="text-xs whitespace-pre-wrap break-words">{result.detail}</pre>
          </div>
        )}
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// /admin/sessions/:organizer_token/config — session runtime config editor
//   ADR-043 + ADR-044. Admin-only. Loads the session by organizer_token,
//   reads the 22-field config from `sessions`, renders the DR1-editable
//   subset (10 fields — the other 12 are either gated, projection visuals
//   not yet wired, or timezone which is creation-only per ADR-044 §Decision 6).
//
//   Submit POSTs to `update-session-config` edge fn, which diffs + writes
//   only changed fields + writes to session_config_changes audit table.
//   Per-field validation errors surface inline.
//
//   Authored: 2026-04-24 Session 25 §P1 Step 11 (ADR-044 Sequence A).
// ---------------------------------------------------------------------------

// Fields we expose to edit on this form. Labels, bounds (client-side),
// grouping, and ordering all live here — one place to change when we add
// or remove a field.
const CONFIG_FORM_FIELDS = [
  // Wall-clock boundaries (ADR-044)
  { key: 'submission_window_ends_at', label: 'Submission window closes', kind: 'datetime-local', group: 'boundaries', hint: 'Wall-clock time submissions stop being accepted.' },
  { key: 'discussion_ends_at', label: 'Discussion ends', kind: 'datetime-local', group: 'boundaries', hint: 'Wall-clock ceiling. Operator-editable mid-session.' },

  // Classifier / clustering
  { key: 'cluster_cap', label: 'Max members per group (cluster cap)', kind: 'int', min: 2, max: 30, group: 'classifier' },
  { key: 'max_groups', label: 'Max groups per session', kind: 'int', min: 1, max: 100, group: 'classifier' },
  { key: 'classifier_confidence_fallback_threshold', label: 'Classifier confidence fallback threshold', kind: 'numeric', min: 0, max: 1, step: 0.05, group: 'classifier', hint: 'Below this confidence, Haiku output escalates to Sonnet.' },
  { key: 'merge_check_enabled', label: 'Merge-check enabled', kind: 'boolean', group: 'classifier', hint: 'Kill switch (ADR-026). Leave on for normal sessions.' },

  // Auto-merge (ADR-041)
  { key: 'auto_merge_threshold', label: 'Auto-merge threshold', kind: 'int', min: 1, max: 29, group: 'auto_merge', hint: 'Groups smaller than this at end of submit window auto-merge.' },
  { key: 'auto_merge_confidence_threshold', label: 'Auto-merge confidence threshold', kind: 'numeric', min: 0, max: 1, step: 0.05, group: 'auto_merge' },

  // Countdown alert
  { key: 'countdown_alert_window_seconds', label: 'Countdown alert window (seconds)', kind: 'int', min: 1, max: 7200, group: 'countdown', hint: 'Amber pulse kicks in this many seconds before discussion_ends_at.' },
  { key: 'countdown_alert_pulse_interval_seconds', label: 'Countdown pulse interval (seconds)', kind: 'int', min: 1, max: 60, group: 'countdown' },
]

const CONFIG_GROUP_LABELS = {
  boundaries: 'Wall-clock boundaries',
  classifier: 'Classifier / clustering',
  auto_merge: 'Auto-merge',
  countdown: 'Countdown alert',
}

// SELECT list for loading current config client-side. Admin RLS on sessions
// allows admin SELECT. Keep in sync with CONFIG_FORM_FIELDS.
const CONFIG_SELECT_COLUMNS = [
  'id', 'code', 'name', 'organizer_token', 'start_time', 'timezone',
  'submission_window_ends_at', 'discussion_ends_at',
  'max_members_per_group', 'max_groups', 'classifier_confidence_fallback_threshold',
  'merge_check_enabled',
  'auto_merge_threshold', 'auto_merge_confidence_threshold',
  'countdown_alert_window_seconds', 'countdown_alert_pulse_interval_seconds',
].join(', ')

// Convert a DB row to the adapter shape the form uses. For datetime-local
// inputs, we need "YYYY-MM-DDTHH:MM" strings; for everything else, pass
// through. cluster_cap aliases max_members_per_group per ADR-043.
function dbRowToFormState(row) {
  const toLocalInput = (iso) => {
    if (!iso) return ''
    return formatLocalDateTimeInput(new Date(iso))
  }
  return {
    submission_window_ends_at: toLocalInput(row.submission_window_ends_at),
    discussion_ends_at: toLocalInput(row.discussion_ends_at),
    cluster_cap: row.max_members_per_group ?? 10,
    max_groups: row.max_groups ?? 30,
    classifier_confidence_fallback_threshold: row.classifier_confidence_fallback_threshold ?? 0.4,
    merge_check_enabled: Boolean(row.merge_check_enabled),
    auto_merge_threshold: row.auto_merge_threshold ?? 3,
    auto_merge_confidence_threshold: row.auto_merge_confidence_threshold ?? 0.6,
    countdown_alert_window_seconds: row.countdown_alert_window_seconds ?? 300,
    countdown_alert_pulse_interval_seconds: row.countdown_alert_pulse_interval_seconds ?? 3,
  }
}

// Build the `config` payload for update-session-config. Coerce datetime-local
// back to ISO string. Coerce numeric inputs. Drop unchanged fields.
function buildConfigPayload(formState, initialState) {
  const payload = {}
  for (const field of CONFIG_FORM_FIELDS) {
    const current = formState[field.key]
    const initial = initialState[field.key]
    if (current === initial) continue

    if (field.kind === 'datetime-local') {
      payload[field.key] = current ? new Date(current).toISOString() : null
    } else if (field.kind === 'int') {
      const n = Number(current)
      if (Number.isFinite(n)) payload[field.key] = Math.round(n)
    } else if (field.kind === 'numeric') {
      const n = Number(current)
      if (Number.isFinite(n)) payload[field.key] = n
    } else if (field.kind === 'boolean') {
      payload[field.key] = Boolean(current)
    }
  }
  return payload
}

function AdminSessionConfig() {
  const { organizer_token } = useParams()
  const [loadState, setLoadState] = useState('loading')
  const [loadError, setLoadError] = useState(null)
  const [session, setSession] = useState(null)
  const [initialState, setInitialState] = useState(null)
  const [formState, setFormState] = useState(null)
  const [saveStatus, setSaveStatus] = useState('idle')
  const [saveError, setSaveError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [lastSavedKeys, setLastSavedKeys] = useState([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoadState('loading')
      setLoadError(null)
      const { data, error } = await supabase
        .from('sessions')
        .select(CONFIG_SELECT_COLUMNS)
        .eq('organizer_token', organizer_token)
        .maybeSingle()
      if (cancelled) return
      if (error) {
        setLoadState('error')
        setLoadError(error.message)
        return
      }
      if (!data) {
        setLoadState('not_found')
        return
      }
      const form = dbRowToFormState(data)
      setSession(data)
      setInitialState(form)
      setFormState(form)
      setLoadState('loaded')
    }
    load()
    return () => { cancelled = true }
  }, [organizer_token])

  function onField(key, value) {
    setFormState((prev) => ({ ...prev, [key]: value }))
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
    setSaveStatus('idle')
    setSaveError(null)
  }

  async function onSubmit(e) {
    e.preventDefault()
    setSaveError(null)
    setFieldErrors({})
    setSaveStatus('saving')
    try {
      const config = buildConfigPayload(formState, initialState)
      if (Object.keys(config).length === 0) {
        setSaveStatus('noop')
        setLastSavedKeys([])
        return
      }
      const { data, error: fnError } = await supabase.functions.invoke('update-session-config', {
        body: { session_id: session.id, config },
      })
      if (fnError) {
        const envelope = await readErrorEnvelope(fnError)
        if (envelope?.error === 'validation_failed' && Array.isArray(envelope.field_errors)) {
          const map = {}
          for (const fe of envelope.field_errors) { map[fe.field] = fe.message }
          setFieldErrors(map)
          setSaveError('Fix the highlighted fields and save again.')
        } else {
          setSaveError(envelope?.detail || envelope?.error || fnError.message || 'Save failed.')
        }
        setSaveStatus('error')
        return
      }
      const updatedKeys = data?.updated_fields ?? []
      setLastSavedKeys(updatedKeys)
      // Re-seed initial + form from the server's current_config so the next
      // diff starts from the freshly-saved snapshot.
      if (data?.current_config) {
        // current_config has Date objects (serialized as ISO strings in JSON).
        // Reshape to form state using the same mapper used on load — the
        // server returns adapter vocabulary, so we reuse dbRowToFormState-
        // like coercion inline.
        const cc = data.current_config
        const nextState = {
          submission_window_ends_at: cc.submission_window_ends_at
            ? formatLocalDateTimeInput(new Date(cc.submission_window_ends_at))
            : '',
          discussion_ends_at: cc.discussion_ends_at
            ? formatLocalDateTimeInput(new Date(cc.discussion_ends_at))
            : '',
          cluster_cap: cc.cluster_cap,
          max_groups: cc.max_groups,
          classifier_confidence_fallback_threshold: cc.classifier_confidence_fallback_threshold,
          merge_check_enabled: Boolean(cc.merge_check_enabled),
          auto_merge_threshold: cc.auto_merge_threshold,
          auto_merge_confidence_threshold: cc.auto_merge_confidence_threshold,
          countdown_alert_window_seconds: cc.countdown_alert_window_seconds,
          countdown_alert_pulse_interval_seconds: cc.countdown_alert_pulse_interval_seconds,
        }
        setInitialState(nextState)
        setFormState(nextState)
      }
      setSaveStatus('saved')
    } catch (e) {
      setSaveError(e?.message || 'Unexpected error. Try again.')
      setSaveStatus('error')
    }
  }

  if (loadState === 'loading') {
    return (
      <AdminFrame title="Session config">
        <p className="text-hap-muted" role="status" aria-live="polite">Loading…</p>
      </AdminFrame>
    )
  }
  if (loadState === 'not_found') {
    return (
      <AdminFrame title="Session not found">
        <p className="text-hap-muted">No session matches that organizer token.</p>
        <Link to="/admin" className="text-sm underline underline-offset-2">Back to admin</Link>
      </AdminFrame>
    )
  }
  if (loadState === 'error') {
    return (
      <AdminFrame title="Session config">
        <p className="text-hap-danger text-sm">Load error: {loadError}</p>
      </AdminFrame>
    )
  }

  const disabled = saveStatus === 'saving'

  // Group fields for rendering.
  const fieldsByGroup = {}
  for (const f of CONFIG_FORM_FIELDS) {
    if (!fieldsByGroup[f.group]) fieldsByGroup[f.group] = []
    fieldsByGroup[f.group].push(f)
  }

  return (
    <AdminFrame title={`Config — ${session.name}`}>
      <header className="space-y-1 pb-4 border-b border-hap-border">
        <p className="text-xs uppercase tracking-widest text-hap-muted">Session</p>
        <h1 className="text-2xl font-semibold">{session.name} <span className="text-hap-muted">({session.code})</span></h1>
        <p className="text-sm text-hap-muted">Timezone: {session.timezone} · Start: {formatSessionTime(session.start_time, session.timezone)}</p>
        <div className="pt-2 text-sm">
          <Link to={`/admin/sessions/${organizer_token}`} className="underline underline-offset-2 text-hap-muted hover:text-hap-ink">
            ← Back to session
          </Link>
        </div>
      </header>

      <form onSubmit={onSubmit} className="space-y-6 pt-4" noValidate>
        {Object.entries(fieldsByGroup).map(([group, fields]) => (
          <fieldset key={group} className="space-y-3">
            <legend className="text-sm font-semibold uppercase tracking-wide text-hap-muted">
              {CONFIG_GROUP_LABELS[group] || group}
            </legend>
            {fields.map((field) => {
              const value = formState[field.key]
              const fieldErr = fieldErrors[field.key]
              const common = {
                value: field.kind === 'boolean' ? undefined : (value ?? ''),
                onChange: (e) => onField(
                  field.key,
                  field.kind === 'boolean' ? e.target.checked : e.target.value,
                ),
                disabled,
                className: `w-full rounded border ${fieldErr ? 'border-hap-danger' : 'border-hap-border'} bg-hap-surface px-3 py-2 text-hap-ink focus:outline-none focus:ring-2 focus:ring-hap-accent disabled:opacity-50`,
              }
              return (
                <label key={field.key} className="block space-y-1">
                  <span className="text-sm font-medium">{field.label}</span>
                  {field.kind === 'boolean' ? (
                    <span className="flex items-center gap-2 pt-1">
                      <input
                        type="checkbox"
                        checked={Boolean(value)}
                        onChange={(e) => onField(field.key, e.target.checked)}
                        disabled={disabled}
                        className="h-4 w-4 rounded border-hap-border text-hap-accent focus:ring-hap-accent disabled:opacity-50"
                      />
                      <span className="text-sm text-hap-ink">{value ? 'enabled' : 'disabled'}</span>
                    </span>
                  ) : field.kind === 'datetime-local' ? (
                    <input type="datetime-local" {...common} />
                  ) : field.kind === 'numeric' ? (
                    <input type="number" step={field.step ?? 'any'} min={field.min} max={field.max} {...common} />
                  ) : (
                    <input type="number" step="1" min={field.min} max={field.max} {...common} />
                  )}
                  {field.hint ? <span className="text-xs text-hap-muted">{field.hint}</span> : null}
                  {fieldErr ? <span className="text-xs text-hap-danger">{fieldErr}</span> : null}
                </label>
              )
            })}
          </fieldset>
        ))}

        {saveError ? (
          <p role="alert" className="text-sm text-hap-danger">{saveError}</p>
        ) : null}
        {saveStatus === 'saved' && lastSavedKeys.length > 0 ? (
          <p className="text-sm text-hap-success" role="status" aria-live="polite">
            Saved {lastSavedKeys.length} field{lastSavedKeys.length === 1 ? '' : 's'}: {lastSavedKeys.join(', ')}.
          </p>
        ) : null}
        {saveStatus === 'noop' ? (
          <p className="text-sm text-hap-muted" role="status">No changes to save.</p>
        ) : null}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={disabled}
            className="flex-1 rounded bg-hap-accent px-4 py-2 font-medium text-white disabled:opacity-50"
          >
            {disabled ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </AdminFrame>
  )
}

// ---------------------------------------------------------------------------

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/join" element={<Join />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminGuard><AdminLanding /></AdminGuard>} />
      <Route path="/admin/sessions/new" element={<AdminGuard><AdminCreateSession /></AdminGuard>} />
      <Route path="/admin/sessions/:organizer_token" element={<AdminGuard><AdminSessionDetail /></AdminGuard>} />
      <Route path="/admin/sessions/:organizer_token/config" element={<AdminGuard><AdminSessionConfig /></AdminGuard>} />
      {/* ADR-034/035/036: 3D brain projection surface, behind AdminGuard,
          lazy-loaded for code-split (no three.js in any other bundle). */}
      <Route
        path="/present/:organizer_token"
        element={
          <AdminGuard>
            <Suspense
              fallback={
                <div className="min-h-screen bg-[#1a1830] text-white flex items-center justify-center">
                  Loading projection bundle…
                </div>
              }
            >
              <PresentProjection />
            </Suspense>
          </AdminGuard>
        }
      />
      <Route path="/_probe" element={<SupabaseProbe />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
